/**
 * SegmentationAnnotationPanel — Right sidebar for segmentation annotation workflow
 *
 * Features:
 * - Navigation header (prev/next case, case counter) — same pattern as LabelingPanel
 * - List of saved annotations with segment color swatch, category badge, measurements
 * - "Add Annotation" button to start a new segment
 * - Inline SegmentationAnnotationForm for attribute entry
 * - Auto-interpolation (intermediate slices filled automatically after 2+ keyframes)
 * - Save button (fires useSaveAnnotationMutation)
 */

import { useState, useCallback, useMemo, useEffect, useRef, type MutableRefObject } from "react";
import { useSavedMaskContourLoading } from "@/hooks/useSavedMaskContourLoading";
import { useSegmentationToolActions } from "@/hooks/useSegmentationToolActions";
import type { CaseLockReason } from "@/utils/caseStatus";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  activeSegmentIndexAtom,
  annotationStartTsAtom,
  derivedMeasurementsAtom,
  isDrawingAtom,
  isSegmentationActiveAtom,
  lockedSeriesIdAtom,
  contourVersionAtom,
  hasFirstStrokeAtom,
  savedAnnotationsAtom,
  segmentationVolumeIdAtom,
  selectedAnnotationIdAtom,
  type AnnotationRecord,
  segmentationAssistAtom,
  bumpRadiusAtom,
  BUMP_RADII,
  requestAnnotationStartAtom,
  saveAnnotationRequestAtom,
} from "../../atoms/segmentationAtoms";
import {
  activeMeasurementToolAtom,
  annotationSaveCountAtom,
  previousDrawingToolAtom,
  type MeasurementToolMode,
} from "../../atoms/measurementAtoms";
import { isV2ToolEnabled } from "../../utils/viewportToolSetup";
import {
  ANNOTATION_SCHEMAS,
  formatOptionLabel,
  organEnabledForLabels,
  organGateActiveForSaved,
  resolveAnnotationSchema,
} from "../../config/annotationSchemas";
import type {
  SegmentationTypeSchema,
  AnnotationSchemaMap,
  AnnotationValue,
} from "../../config/annotationSchemas";
import { SegmentationAnnotationForm, isFormComplete } from "./SegmentationAnnotationForm";
import {
  getCornerstone,
  getCornerstoneTools,
  findViewportForSeries,
} from "../../utils/dicomLoader";
import { currentSliceAtom, instanceToSliceMapAtom } from "../../atoms/selectionAtoms";
import { allSlotSeriesSlicesSelector } from "../../atoms/hangingProtocolAtoms";
import { emit as busEmit } from "../../lib/viewerBus";
import {
  useSegmentationAssistQuery,
  useAnnotationsQuery,
  useSaveAnnotationMutation,
  useDeleteAnnotationMutation,
  useMarkCompleteMutation,
  useUnmarkCompleteMutation,
  useCasesQuery,
  type AnnotationRecordResponse,
  type AnnotationInput,
  type AnnotationSaveResponse,
  type CompanionLabelmapInput,
  type HullStatsInput,
} from "../../hooks/useLabelingQueries";
import { useAnnotationActiveTime } from "../../hooks/useAnnotationActiveTime";
import { useAnnotationSessionEvents } from "../../hooks/useAnnotationSessionEvents";
import { loadMaskAsContours } from "../../utils/niftiMaskLoader";
import { companionLabelOrders, labelOrdersOf } from "../../utils/savedStoneOrder";
import {
  capturePredictionMaskEditSnapshot,
  restorePredictionMaskEditSnapshot,
  createPredictionMaskOverlayState,
  type PredictionMaskEditSnapshot,
} from "@/utils/predictionMaskOverlay";
import { formatSegmentationCondition } from "@/predictions/segmentationCondition";
import { decideSync } from "../../utils/syncGate";
import {
  getBumpedImageIds,
  resetBumpTracking,
  setBumpSegmentIndex,
} from "../../tools/BumpContourTool";
import {
  getRegionGrowImageIds,
  resetRegionGrowTracking,
  resetRegionGrowState,
  setNnInteractiveContext,
  setRegionGrowConfig,
  setRegionGrowSegmentIndex,
  endNnSession,
} from "../../tools/RegionGrowTool";
import { clearUndo, beginUndoSession } from "../../tools/annotationUndo";
import { getNnRequestInFlight } from "../../tools/nnInteractiveSession";
import type { PredictionEditCompletion } from "../../hooks/usePredictionMaskOverlay";
import { clearSliceHUCache } from "../../components/hanging-protocol/SegmentationStatsOverlay";
import {
  ACTIVE_SEGMENT_COLOR,
  SAVED_SEGMENT_COLOR,
  ORGAN_SEGMENT_COLOR,
  ORGAN_SEGMENT_INDEX,
  setSegmentColor,
} from "../../utils/segmentationConstants";
import { getContourSegmentationId } from "../../utils/viewportToolSetup";
import {
  nnSegmentationResultAtom,
  nnClickMarkersAtom,
  segEngineAtom,
  attributeViewModeAtom,
  maskFillAlphaAtom,
  companionMasksBlockedAtom,
} from "../../atoms/segmentationAtoms";
import { emit } from "../../lib/viewerBus";
import {
  READER_CONFIDENCE_HINT,
  READER_CONFIDENCE_KEY,
  parseReaderConfidence,
} from "../../utils/readerConfidence";
import { ReaderConfidenceInput } from "./ReaderConfidenceInput";
import { hullVolumeMm3Atom, hullAxesAtom } from "../hanging-protocol/SegmentationViewer3D";
import { getSiblingViewports, showNnToast } from "../../tools/nnInteractiveSession";
import { rasterizePolygon, worldToPixel } from "../../utils/contourUtils";
import { StackGeometryError, saveGeometryForViewport } from "../../utils/stackSliceGeometry";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCrosshairs,
  FaLayerGroup,
  FaPlus,
  FaTrash,
  FaSave,
  FaCheck,
  FaFileAlt,
  FaEdit,
  FaStepForward,
} from "../../utils/icons";
import { WorkListButton } from "./WorkListButton";
import { InstructionsDialog } from "./InstructionsDialog";
import { PhaseTabBar } from "../hanging-protocol/PhaseTabBar";
import { projectGatingModeAtom } from "../../atoms/gatingAtoms";
import { GATING_MODE } from "../../types/gating";
import { logger } from "@/utils/logger";
import { isLoadedFromMask, setLoadedFromMask } from "../../utils/annotationFlags";
import {
  collectAnnotationSegmentIndices,
  createAnnotationLocalIndexMap,
  nextAnnotationSegmentIndex,
} from "../../utils/annotationSegmentIndex";
import { UnassessableCaseControl } from "./UnassessableCaseControl";

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of retries for loading NIfTI mask data before giving up. */
const MAX_MASK_LOAD_RETRIES = 3;

const FALLBACK_COLORS = [
  "#22c55e",
  "#f59e0b",
  "#ec4899",
  "#06b6d4",
  "#f97316",
  "#8b5cf6",
  "#14b8a6",
  "#f43f5e",
];

/** Stable color per category — reads from schema `color` field, falls back to rotating palette. */
const _categoryColorCache: Record<string, string> = {};
let _nextFallback = 0;

/**
 * Build a sparse labelmap from contour annotations belonging to a single viewport/series.
 * Finds the owning viewport, rasterizes each contour, and returns the labelmap + metadata.
 */
function buildLabelmapFromContours(
  contours: any[],
  cornerstone: any,
  getBumpedImageIds: () => Set<string>,
  getRegionGrowImageIds: () => Set<string>,
): {
  labelmap: Record<string, number[][]>;
  volumeShape: [number, number, number];
  spacing: [number, number, number];
  /** vtkImageData origin (3 floats) at save time. Saved alongside the mask
   *  so the load path can rebuild world coords deterministically without
   *  depending on per-slice IPP metadata, which can be inconsistent on
   *  reformatted cor/sag MPR series. */
  origin: [number, number, number];
  /** vtkImageData direction matrix (9 floats, column-major) at save time. */
  direction: number[];
  manualSlices: Set<number>;
  interpolatedSlices: Set<number>;
  bumpedSlices: Set<number>;
  regionGrowSlices: Set<number>;
} | null {
  if (contours.length === 0) return null;

  const engines = cornerstone.getRenderingEngines?.() ?? [];
  let viewport: any = null;
  const firstImageId = contours[0]?.metadata?.referencedImageId ?? "";

  // Find the viewport that owns these contours
  for (const engine of engines) {
    for (const vp of engine.getViewports?.() ?? []) {
      const vpIds: string[] = (vp as any).getImageIds?.() ?? [];
      if (vpIds.length > 0 && vpIds.includes(firstImageId)) {
        viewport = vp;
        break;
      }
    }
    if (viewport) break;
  }
  if (!viewport) return null;

  const imageData = (viewport as any).getImageData?.();
  const imgRows = imageData?.dimensions?.[1] ?? imageData?.rows ?? 0;
  const imgCols = imageData?.dimensions?.[0] ?? imageData?.cols ?? 0;
  const vpImageIds: string[] = viewport.getImageIds?.() ?? [];
  const numSlices = vpImageIds.length;

  // Capture spacing + origin + direction now so the backend can write a
  // faithful NIfTI affine. Without origin/direction the saved NIfTI's affine
  // collapses to a positive-diagonal scale, which loses orientation entirely on
  // reformatted MPR series. A stack's vtkImageData is one image (slice spacing
  // 0.001, displayed IPP as origin), so a stack takes its source volume's frame
  // instead (ENG-258); that throws StackGeometryError rather than guess. Its
  // slices may run opposite the stack's list, so toFileSlice maps each contour's
  // stack index onto the volume's: every save of a series then writes the same
  // header and the same voxel slice order.
  const { spacing, origin, direction, toFileSlice } = saveGeometryForViewport(
    viewport,
    () => cornerstone.cache?.getVolumes?.() ?? [],
  );

  // Build slice index map
  const seriesMap: Record<string, number> = {};
  vpImageIds.forEach((id: string, idx: number) => {
    seriesMap[id] = idx;
    const filename = id.split("?")[0].split("/").pop() ?? "";
    if (filename) {
      seriesMap[filename] = idx;
      seriesMap[`${filename}.dcm`] = idx;
    }
  });

  const labelmap: Record<string, number[][]> = {};
  const manualSlices = new Set<number>();
  const interpolatedSlices = new Set<number>();
  const bumpedSlices = new Set<number>();
  const regionGrowSlices = new Set<number>();
  const bumpedIds = getBumpedImageIds();
  const regionGrowIds = getRegionGrowImageIds();

  for (const ann of contours) {
    const polyline: number[][] = ann.data.contour.polyline;
    const imageId: string = ann.metadata?.referencedImageId ?? "";

    let key = imageId.split("?")[0].split("/").pop() ?? "";
    let viewportSlice = seriesMap[key] ?? seriesMap[`${key}.dcm`];
    if (viewportSlice === undefined) {
      const directIdx = vpImageIds.indexOf(imageId);
      if (directIdx >= 0) viewportSlice = directIdx;
    }
    if (viewportSlice === undefined) continue;
    const sliceIdx = toFileSlice(viewportSlice);

    const isAutoInterpolated = !!ann.autoGenerated;
    const isRegionGrow = regionGrowIds.has(imageId);
    if (isAutoInterpolated && !isRegionGrow) {
      interpolatedSlices.add(sliceIdx);
    } else {
      manualSlices.add(sliceIdx);
    }
    if (bumpedIds.has(imageId)) bumpedSlices.add(sliceIdx);
    if (isRegionGrow) regionGrowSlices.add(sliceIdx);

    // ENG-250: keep vertices continuous. Rounding them to whole pixels saved
    // small stones up to ~40% larger than drawn.
    const pixelPoints: [number, number][] = [];
    const saveVtk = imageData?.imageData;
    for (const worldPt of polyline) {
      try {
        if (saveVtk?.worldToIndex) {
          pixelPoints.push(worldToPixel(saveVtk, worldPt));
        } else {
          const canvasPt = (viewport as any).worldToCanvas(worldPt);
          if (canvasPt) {
            const imagePt = (viewport as any).canvasToIndex
              ? (viewport as any).canvasToIndex(canvasPt)
              : canvasPt;
            pixelPoints.push([imagePt[1], imagePt[0]]);
          }
        }
      } catch {
        /* skip point */
      }
    }
    if (pixelPoints.length < 3) continue;

    const sliceKey = String(sliceIdx);
    if (!labelmap[sliceKey]) labelmap[sliceKey] = [];
    const filledIndices = rasterizePolygon(pixelPoints, imgCols, imgRows);
    for (const idx of filledIndices) {
      const r = Math.floor(idx / imgCols);
      const c = idx % imgCols;
      labelmap[sliceKey].push([r, c]);
    }
  }

  if (Object.keys(labelmap).length === 0) return null;

  return {
    labelmap,
    volumeShape: [imgRows, imgCols, numSlices],
    spacing,
    origin,
    direction,
    manualSlices,
    interpolatedSlices,
    bumpedSlices,
    regionGrowSlices,
  };
}

function getCategoryColor(category: string): string {
  if (_categoryColorCache[category]) return _categoryColorCache[category];
  const schemaColor = ANNOTATION_SCHEMAS[category]?.color;
  if (schemaColor) {
    _categoryColorCache[category] = schemaColor;
    return schemaColor;
  }
  _categoryColorCache[category] = FALLBACK_COLORS[_nextFallback % FALLBACK_COLORS.length];
  _nextFallback++;
  return _categoryColorCache[category];
}

/** Build a short summary string from saved attributes (excluding bosniak_class which has its own badge). */
export function formatAttributeSummary(
  attributes: Record<string, AnnotationValue>,
  schema: { fields: Record<string, { display_name: string; unit?: string }> } | undefined,
): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(attributes)) {
    if (key === "bosniak_class") continue; // already shown as badge
    if (value === "" || value === "none" || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      parts.push(value.map((v) => formatOptionLabel(String(v))).join(", "));
      continue;
    }
    const field = schema?.fields[key];
    const label =
      typeof value === "boolean"
        ? (field?.display_name ?? key)
        : typeof value === "number"
          ? `${value}${field?.unit ? ` ${field.unit}` : ""}`
          : formatOptionLabel(String(value));
    parts.push(label);
  }
  return parts.length > 0 ? parts.slice(0, 3).join(" · ") : "—";
}

// ============================================================================
// Props
// ============================================================================

interface GtHints {
  finding_count: number;
  findings: Array<{
    finding_type: string;
    laterality?: string;
    size?: string;
    renal_location?: string;
    lesion_multiplicity?: string;
    urinary_tract_location?: string;
    // Lymph-node locators (ly3d): station is the primary locator.
    nodal_station?: string;
    malignancy_suspicion?: string;
  }>;
}

interface SegmentationAnnotationPanelProps {
  projectId: string;
  caseId: string;
  studyInstanceUid: string;
  seriesInstanceUid?: string;
  savedEditRequest?: { annotationId: string; requestId: number } | null;
  /** Axial series UID — always used for nnInteractive regardless of active viewport */
  axialSeriesUid?: string;
  onNext: () => void;
  onPrevious: () => void;
  onNextUnlabeled?: () => void;
  onWorkList?: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  hasNextUnlabeled?: boolean;
  currentIndex: number;
  totalCases: number;
  gtHints?: GtHints;
  labelsToAssign?: string[];
  /** Project AI condition keys for display in both segmentation-study arms. */
  conditionLabels?: Readonly<Record<string, string>>;
  /**
   * Worklist's `instructions_key` value — used to fetch authenticated project
   * instructions and gate the Instructions button. Null/undefined hides the
   * button (e.g. CaseViewer's standalone mount has no worklist).
   */
  instructionsKey?: string | null;
  /** Editing a model prediction; preserves origin presentation, not tool permissions. */
  modelInitialized?: boolean;
  /** Existing AI prediction segment to edit in place. Null means add mode. */
  predictionEditSegmentIndex?: number | null;
  /** Persisted annotation backing that prediction, when it was already accepted. */
  predictionEditAnnotationId?: string | null;
  /** Native series containing the AI prediction being edited. */
  predictionEditSeriesInstanceUid?: string | null;
  /** Category emitted for the AI prediction being edited. */
  predictionEditCategory?: string | null;
  /** Capture a checkpoint and release overlay ownership before unlocking. */
  onBeforePredictionEdit?: (
    predictionSegmentIndex: number,
    savedTarget?: { annotationId: string; segmentIndex: number },
  ) => boolean;
  /** Obtain this edit's Cancel/Save callbacks before unlocking its contours. */
  getPredictionEditCompletion?: (segmentIndex: number) => PredictionEditCompletion | null;
  hideLightMode?: boolean;
  /** Compact editor layout and existing starting-tool/form/save behavior. */
  compactStudyMode?: boolean;
  /** Reader-study tool policy; factory/legacy keep their existing entry behavior. */
  studyToolPolicy?: boolean;
  /** Segmentation study: the case is closed, so no annotation can be started or edited. */
  lockReason?: CaseLockReason | null;
  /** Show the engine selector in compact study/factory editors; finding config still gates AI context. */
  allowAiSegmentation?: boolean;
  /** Load saved masks alongside prediction contours, deduplicating by annotation identity. */
  loadSavedMasksByAnnotationId?: boolean;
  /** All reserved prediction indices; undefined means prediction identity is still loading. */
  predictionSegmentIndices?: readonly number[];
  /** Non-clinical provenance persisted with factory model-review annotations. */
  annotationMetadata?: Record<string, string | number | boolean | string[]>;
  /** Notify the parent rail that its persistent controls started a new item. */
  onStartAnnotation?: (mode?: "new" | "edit") => void;
  /** Close the parent study editor after local annotation state resets. */
  onCancelAnnotation?: () => void;
  /** Surface the optimistic saved record so an edited AI card can update in place. */
  onAnnotationSaved?: (annotation: AnnotationRecord) => void;
  saveCandidateAnnotation?: (input: AnnotationInput) => Promise<AnnotationSaveResponse>;
  onCandidateEditStart?: (condition: string, sourceSegmentIndex: number) => boolean;
  /** Receives the open prediction edit's card actions while one is open. */
  predictionEditActionsRef?: MutableRefObject<PredictionEditActions | null>;
  /** Segmentation study: Save requires attributes.reader_confidence, 1–100 (ENG-262). */
  requireReaderConfidence?: boolean;
  /** Saved rating of the accepted prediction being edited, to prefill the field. */
  predictionEditReaderConfidence?: number | null;
  /**
   * Save on a prediction edit whose geometry did not change (ENG-277). "closed":
   * handled and the editor closed; "failed": already reported, editor stays open.
   */
  onUnchangedPredictionSave?: (readerConfidence: number | null) => Promise<"closed" | "failed">;
}

export interface PredictionEditActions {
  /** The editor's valid confidence rating, or null (ENG-262). */
  readerConfidence: number | null;
  /**
   * Save when the geometry changed; otherwise close the editor ("unchanged") so
   * the caller accepts the original prediction. "failed" was already reported.
   */
  accept: () => Promise<"saved" | "unchanged" | "failed">;
  /** Cancel the edit, restoring its checkpoint. False when it stayed open. */
  discard: () => boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

function AnnotationCard({
  annotation,
  isSelected,
  sequenceLabel,
  onSelect,
  onDelete,
  onEdit,
  conditionLabel,
}: {
  annotation: AnnotationRecord;
  isSelected: boolean;
  sequenceLabel: string;
  onSelect: () => void;
  onDelete: () => void;
  onEdit: () => void;
  conditionLabel?: string;
}) {
  const color = getCategoryColor(annotation.category);
  // Prefer hull volume — matches the 3D viewer overlay and gives a real
  // number for contour-only annotations whose mask volume rounds to 0.
  const volume =
    annotation.hull_stats?.volume_mm3 ?? annotation.segmentation_stats?.volume_mm3 ?? null;
  const longAxis = annotation.measurements?.long_axis_mm ?? null;
  const schema = ANNOTATION_SCHEMAS[annotation.category];
  const categoryLabel = conditionLabel
    ? formatSegmentationCondition(conditionLabel)
    : (schema?.display_name ?? annotation.category);

  return (
    <div
      onClick={onSelect}
      className={`group sidebar-card rounded-lg px-3 py-2 cursor-pointer transition-all duration-150 ${
        isSelected ? "sidebar-card-active ring-1 ring-primary/30" : "hover:bg-white/5"
      }`}
    >
      <div className="flex items-center gap-2">
        {/* Segment color swatch */}
        <div className="w-3 h-3 rounded-xs shrink-0" style={{ backgroundColor: color }} />

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-white/90 truncate">
              {categoryLabel} {sequenceLabel}
            </span>
            {annotation.attributes.bosniak_class && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/20 text-primary-foreground font-semibold">
                B{String(annotation.attributes.bosniak_class)}
              </span>
            )}
          </div>
          <div className="text-[9px] text-fg-muted tabular-nums mt-0.5">
            {volume != null && volume > 0
              ? `${volume.toFixed(0)} mm³${longAxis != null && longAxis > 0 ? ` · ${longAxis.toFixed(1)} mm` : ""}`
              : formatAttributeSummary(annotation.attributes, schema) || "—"}
          </div>
        </div>

        {/* Edit / Delete — visible on hover */}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="sidebar-btn w-6 h-6 shrink-0 hover:text-signal-text-tint"
            title="Edit annotation"
          >
            <FaEdit className="w-2.5 h-2.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="sidebar-btn w-6 h-6 shrink-0 hover:text-warn"
            title="Delete annotation"
          >
            <FaTrash className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SegmentationAnnotationPanel({
  projectId,
  caseId: _caseId,
  studyInstanceUid,
  seriesInstanceUid,
  savedEditRequest,
  axialSeriesUid: _axialSeriesUid,
  onNext,
  onPrevious,
  onNextUnlabeled,
  onWorkList,
  hasNext,
  hasPrevious,
  hasNextUnlabeled,
  currentIndex,
  totalCases,
  gtHints,
  labelsToAssign,
  conditionLabels,
  instructionsKey = null,
  modelInitialized = false,
  predictionEditSegmentIndex = null,
  predictionEditAnnotationId = null,
  predictionEditSeriesInstanceUid = null,
  predictionEditCategory = null,
  onBeforePredictionEdit,
  getPredictionEditCompletion,
  hideLightMode = false,
  compactStudyMode = false,
  studyToolPolicy = false,
  lockReason = null,
  allowAiSegmentation = false,
  loadSavedMasksByAnnotationId = false,
  predictionSegmentIndices,
  annotationMetadata,
  onStartAnnotation,
  onCancelAnnotation,
  onAnnotationSaved,
  saveCandidateAnnotation,
  onCandidateEditStart,
  predictionEditActionsRef,
  requireReaderConfidence = false,
  predictionEditReaderConfidence = null,
  onUnchangedPredictionSave,
}: SegmentationAnnotationPanelProps) {
  // Instructions
  const [showInstructions, setShowInstructions] = useState(false);

  // Atoms
  const [savedAnnotations, setSavedAnnotations] = useAtom(savedAnnotationsAtom);
  const [selectedAnnotationId, setSelectedAnnotationId] = useAtom(selectedAnnotationIdAtom);
  const [activeSegmentIndex, setActiveSegmentIndex] = useAtom(activeSegmentIndexAtom);
  const segmentationVolumeId = useAtomValue(segmentationVolumeIdAtom);
  // Starting a stroke session: set the atom AND Cornerstone's active segment
  // together. The pane only syncs Cornerstone when the atom value changes, so a
  // claim equal to the pre-seeded value (or a later overwrite by another writer)
  // left new strokes on another index and Save counted 0 slices (ENG-248).
  const activateSegment = useCallback(
    (segmentIndex: number) => {
      setActiveSegmentIndex(segmentIndex);
      if (!segmentationVolumeId) return;
      try {
        getCornerstoneTools().segmentation?.segmentIndex?.setActiveSegmentIndex?.(
          getContourSegmentationId(segmentationVolumeId),
          segmentIndex,
        );
      } catch (err) {
        logger.debug("[activateSegment] Cornerstone not ready; the pane effect syncs later:", err);
      }
    },
    [segmentationVolumeId, setActiveSegmentIndex],
  );
  const savedEditCheckpoint = useRef<{
    study: string;
    snapshot: PredictionMaskEditSnapshot;
  } | null>(null);
  const derivedMeasurements = useAtomValue(derivedMeasurementsAtom);
  const gatingMode = useAtomValue(projectGatingModeAtom);
  const annotationStartTs = useAtomValue(annotationStartTsAtom);
  const setAnnotationStartTs = useSetAtom(annotationStartTsAtom);
  // Idle-excluded active time for the in-progress annotation (subtracts >60s
  // inactivity gaps and tab-hidden spans from the start→save window).
  const { getActiveSeconds } = useAnnotationActiveTime(annotationStartTs);
  // Keep the persisted identity for an atomic in-place edit.
  const editingAnnotationIdRef = useRef<string | null>(null);
  // ENG-202: annotation edit windows for the case-time event log (study route only).
  const { saveStamp: annotationSaveStamp, markSaved: markAnnotationSaved } =
    useAnnotationSessionEvents(
      annotationStartTs,
      editingAnnotationIdRef,
      studyInstanceUid.split("::")[0],
    );
  const isDrawing = useAtomValue(isDrawingAtom);
  const setIsDrawing = useSetAtom(isDrawingAtom);
  const [isSegmentationActive, setIsSegmentationActive] = useAtom(isSegmentationActiveAtom);
  const setLockedSeriesId = useSetAtom(lockedSeriesIdAtom);
  const [contourVersion, setContourVersion] = useAtom(contourVersionAtom);
  const setHasFirstStroke = useSetAtom(hasFirstStrokeAtom);
  const activeMeasurementTool = useAtomValue(activeMeasurementToolAtom);
  const setActiveMeasurementTool = useSetAtom(activeMeasurementToolAtom);
  const setPreviousDrawingTool = useSetAtom(previousDrawingToolAtom);
  const setDerivedMeasurements = useSetAtom(derivedMeasurementsAtom);
  const [segAssistConfig, setSegmentationAssist] = useAtom(segmentationAssistAtom);
  // Bridge the assist-config atom into RegionGrowTool's module-level state so
  // the tool's preMouseDownCallback sees `_config.enabled` etc. Without this,
  // every click is rejected with "config not loaded or disabled".
  useEffect(() => {
    setRegionGrowConfig(segAssistConfig?.region_grow ?? null);
  }, [segAssistConfig]);
  const setBumpRadius = useSetAtom(bumpRadiusAtom);

  const setNnSegResult = useSetAtom(nnSegmentationResultAtom);
  const setNnClickMarkers = useSetAtom(nnClickMarkersAtom);
  const [segEngine, setSegEngine] = useAtom(segEngineAtom);
  const [attributeViewMode, setAttributeViewMode] = useAtom(attributeViewModeAtom);
  const lightMode = attributeViewMode === "light";
  const [maskFillAlpha, setMaskFillAlpha] = useAtom(maskFillAlphaAtom);
  const hullVolume = useAtomValue(hullVolumeMm3Atom);
  const hullAxes = useAtomValue(hullAxesAtom);
  const companionMasksBlocked = useAtomValue(companionMasksBlockedAtom);
  // Navigation atoms
  const setCurrentSlice = useSetAtom(currentSliceAtom);
  const navigateToSlice = useCallback(
    (slice: number) => {
      setCurrentSlice(slice);
      busEmit("sliceNavRequest", { slice });
    },
    [setCurrentSlice],
  );
  const instanceToSliceMap = useAtomValue(instanceToSliceMapAtom);
  const visibleSeriesSlices = useAtomValue(allSlotSeriesSlicesSelector);
  const visibleSliceKey = useMemo(
    () =>
      visibleSeriesSlices
        .map(({ slotId, seriesId, sliceIndex }) => `${slotId}:${seriesId ?? ""}:${sliceIndex}`)
        .join("|"),
    [visibleSeriesSlices],
  );

  // Apply the user's mask fill-opacity preference to the labeling viewer's
  // contour segmentation style. Same global contour-style API the demo viewer
  // uses for measure mode (CaseViewer), but here the labeling viewer is the
  // sole writer of fillAlpha (it set none before) and restores the prior style
  // on unmount, so this never bleeds into the demo viewer (separate route).
  // Re-runs on slider change; cleanup restores `prev` before each re-apply, so
  // `prev` is always the pre-labeling style rather than our own last value.
  useEffect(() => {
    let restore: (() => void) | null = null;
    try {
      const csTools = getCornerstoneTools();
      const contourType = csTools.Enums?.SegmentationRepresentations?.Contour;
      const segStyle = csTools.segmentation?.config?.style;
      if (contourType && segStyle?.setStyle && segStyle.getStyle) {
        const prev = segStyle.getStyle({ type: contourType }) ?? {};
        segStyle.setStyle(
          { type: contourType },
          {
            ...prev,
            outlineWidth: 1,
            outlineWidthInactive: 1,
            renderFill: true,
            fillAlpha: maskFillAlpha,
          },
        );
        // Re-render so already-drawn masks pick up the new opacity immediately.
        getCornerstone()
          .getRenderingEngines?.()
          ?.forEach((engine: any) => {
            try {
              engine.render();
            } catch (err) {
              logger.warn("[SegmentationAnnotationPanel] mask-opacity render failed:", err);
            }
          });
        restore = () => {
          try {
            segStyle.setStyle({ type: contourType }, prev, false);
          } catch (err) {
            logger.warn("[SegmentationAnnotationPanel] mask-opacity style restore failed:", err);
          }
        };
      }
    } catch (err) {
      logger.warn("[SegmentationAnnotationPanel] mask-opacity style apply failed:", err);
    }
    return () => {
      restore?.();
    };
  }, [maskFillAlpha]);

  // Static schemas are the single source of truth for field definitions,
  // display names, colors, and visibility. No API fallback.
  // Gate to only schemas matching the task's labels_to_assign (if provided).
  const schemas: AnnotationSchemaMap = useMemo(() => {
    if (!labelsToAssign || labelsToAssign.length === 0) return ANNOTATION_SCHEMAS;
    const allowed = new Set(labelsToAssign);
    const filtered: AnnotationSchemaMap = {};
    for (const [key, s] of Object.entries(ANNOTATION_SCHEMAS)) {
      if (allowed.has(key)) filtered[key] = s;
    }
    return Object.keys(filtered).length > 0 ? filtered : ANNOTATION_SCHEMAS;
  }, [labelsToAssign]);

  // Segmentation assist config (V2 tool behavior per annotation type)
  const segAssistQuery = useSegmentationAssistQuery();

  // API hooks
  const annotationsQuery = useAnnotationsQuery(studyInstanceUid, projectId);
  const localIndexMap = useMemo(
    () => createAnnotationLocalIndexMap(),
    [projectId, studyInstanceUid, loadSavedMasksByAnnotationId],
  );
  const currentLocalIndexMap = useRef<ReturnType<typeof createAnnotationLocalIndexMap> | null>(
    localIndexMap,
  );
  useEffect(() => {
    currentLocalIndexMap.current = localIndexMap;
    return () => {
      currentLocalIndexMap.current = null;
    };
  }, [localIndexMap]);
  const prepareLocalIndices = useCallback(() => {
    if (!loadSavedMasksByAnnotationId) return;
    if (!predictionSegmentIndices) throw new Error("Prediction indices are not ready");
    localIndexMap.reserve(predictionSegmentIndices);
    const live = getCornerstoneTools().annotation.state.getAllAnnotations() ?? [];
    localIndexMap.reserve(collectAnnotationSegmentIndices(live));
    // Preserve geometry already saved in this visit, including edits of raw AI.
    for (const ann of live) {
      if (ann.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
      const id = ann.data?.savedAnnotationId;
      const index = (ann.data?.segmentation as { segmentIndex?: number })?.segmentIndex;
      if (typeof id === "string" && typeof index === "number") localIndexMap.bind(id, index);
    }
    for (const annotation of annotationsQuery.data?.annotations ?? []) {
      if (
        annotation.category === "organ" &&
        predictionSegmentIndices.includes(ORGAN_SEGMENT_INDEX)
      ) {
        throw new Error("Prediction index conflicts with the reserved organ index");
      }
      localIndexMap.ensure(annotation.annotation_id, annotation.category === "organ");
    }
  }, [
    loadSavedMasksByAnnotationId,
    predictionSegmentIndices,
    localIndexMap,
    annotationsQuery.data,
  ]);
  const resolveAnnotationLocalIndex = useCallback(
    (annotation: AnnotationRecord): number | null => {
      if (!loadSavedMasksByAnnotationId) return annotation.segment_index;
      try {
        return localIndexMap.resolve(annotation.annotation_id);
      } catch (error) {
        logger.warn("[annotation index] missing viewer mapping", error);
        emit("toast", { message: "Saved mask is not ready. Please wait or reload the case." });
        return null;
      }
    },
    [loadSavedMasksByAnnotationId, localIndexMap],
  );
  const saveAnnotationMutation = useSaveAnnotationMutation(
    projectId,
    studyInstanceUid,
    saveCandidateAnnotation,
  );
  const deleteAnnotationMutation = useDeleteAnnotationMutation(projectId, studyInstanceUid);
  const markCompleteMutation = useMarkCompleteMutation(projectId, studyInstanceUid);
  const unmarkCompleteMutation = useUnmarkCompleteMutation(projectId, studyInstanceUid);
  // #726: is THIS case already marked complete? Drives the footer between
  // "Mark Complete" and a "Completed — Unmark" state, so revisiting a finished
  // case no longer shows "Mark Complete" as if it were unlabeled.
  const casesQuery = useCasesQuery(projectId);
  const currentCaseStatus = casesQuery.data?.cases.find(
    (c) => c.study_instance_uid === studyInstanceUid.split("::")[0],
  );
  const isCaseComplete = currentCaseStatus?.completion_status === "complete";
  const isCaseUnassessable = currentCaseStatus?.completion_status === "unassessable";
  // Study lock: every start point below refuses, so drawing tools and their
  // hotkeys (which need an active annotation) stay off too.
  const caseLocked = lockReason !== null;
  const [undoVisible, setUndoVisible] = useState(false);
  const markCompleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mark this case complete, then briefly surface the Undo affordance before
  // advancing. Shared by the normal "Mark Complete" button and the
  // "Skip — numerous stones" escape hatch (an empty case a reader can't
  // reasonably annotate); both hit the same POST .../mark-complete endpoint.
  // `reason` is passed only by the skip path (e.g. "numerous_stones") so the
  // backend can record why the case was completed without annotation.
  const completeCase = useCallback(
    (reason?: string) => {
      markCompleteMutation.mutate(reason, {
        onSuccess: () => {
          setUndoVisible(true);
          const timer = setTimeout(() => {
            setUndoVisible(false);
            if (onNextUnlabeled && hasNextUnlabeled) {
              onNextUnlabeled();
            } else if (hasNext) {
              onNext();
            }
          }, 500);
          markCompleteTimerRef.current = timer;
        },
      });
    },
    [markCompleteMutation, onNextUnlabeled, hasNextUnlabeled, hasNext, onNext],
  );

  const predictionEditCompletionRef = useRef<PredictionEditCompletion | null>(null);

  // Reset all segmentation state on case change and on unmount.
  // Ensures no orphan drawing state survives navigation or page refresh.
  const resetSegmentationState = useCallback(() => {
    predictionEditCompletionRef.current = null;
    setIsSegmentationActive(false);
    setLockedSeriesId(null);
    setHasFirstStroke(false);
    setIsDrawing(false);
    setActiveMeasurementTool("none");
    setPreviousDrawingTool(null);
    setAnnotationStartTs(null);
    setDerivedMeasurements(null);
    setSegmentationAssist(null);
    clearSliceHUCache();
    // Hygiene on case change / unmount (incl. leaving labeling for the demo
    // viewer, which mounts the same Ctrl+Z consumer): drop the undo log so it
    // can't be drained cross-route. The scope key already keeps stale entries
    // inert, so this is hygiene, not correctness.
    clearUndo();
  }, [
    setIsSegmentationActive,
    setLockedSeriesId,
    setHasFirstStroke,
    setIsDrawing,
    setActiveMeasurementTool,
    setPreviousDrawingTool,
    setAnnotationStartTs,
    setDerivedMeasurements,
    setSegmentationAssist,
  ]);

  useEffect(() => {
    resetSegmentationState();
    savedEditCheckpoint.current = null;
    setIsAddingNew(false);
    setFormValues({});
    setSelectedCategory(null);
    return resetSegmentationState; // also reset on unmount
  }, [studyInstanceUid, projectId, resetSegmentationState]);

  // Drop every PlanarFreehand annotation from cornerstone state on case
  // change, and clear nnSegmentationResult so SegmentationViewerPanel falls
  // back to the empty-state stats panel. Annotation state is global, so
  // without this the next case's 3D viewer keeps rendering the previous
  // case's contours (the labelmap voxels already reload per-volume; only the
  // contour polylines linger).
  //
  // SCOPE: global wipe of every PlanarFreehandContourSegmentation annotation
  // — safe only because labeling is single-case and this panel owns the
  // cornerstone session. If a sibling viewer ever populates contours in
  // parallel, narrow this to the active segmentationId before removing.
  useEffect(() => {
    try {
      const csTools = getCornerstoneTools();
      const annotationState = csTools.annotation.state;
      const allAnns = annotationState.getAllAnnotations() as any[];
      for (const ann of allAnns) {
        if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
        try {
          annotationState.removeAnnotation(ann.annotationUID);
        } catch (err) {
          logger.debug("[case-switch contour clear] removeAnnotation failed:", err);
        }
      }
      setContourVersion((v) => v + 1);
    } catch (err) {
      logger.debug("[case-switch contour clear] cornerstone unavailable:", err);
    }
    // Drop the annotation undo log on case change. Entries (esp. a deleteRestore
    // closure) capture the PREVIOUS study's live annotation objects + viewport
    // element; surviving navigation lets a Ctrl+Z on the next study re-add a
    // stale contour, which handleSave (matches by segmentIndex only) would then
    // save onto the wrong study. clearUndo's contract already lists "case
    // change" as a trigger — this is the missing wire.
    clearUndo();
    setNnSegResult(null);
  }, [studyInstanceUid, projectId, setContourVersion, setNnSegResult]);

  // Sync backend annotations into Jotai atom only on actual query refetches.
  // Gating on dataUpdatedAt (not isSegmentationActive) prevents the effect from
  // re-running when the active flag flips post-save and clobbering the
  // optimistic entry added in saveAnnotationMutation.onSuccess.
  //
  // We *do* still advance the watermark while skipping during a draw —
  // otherwise a periodic refetch that lands mid-draw would queue an "old"
  // sync, which then fires the moment isSegmentationActive flips false
  // (i.e. right after Save) and overwrites the optimistic record with
  // pre-save data. Result: the just-saved annotation disappears from the
  // sidebar despite being saved on the backend.
  const lastSyncedDataUpdatedAtRef = useRef(0);
  useEffect(() => {
    lastSyncedDataUpdatedAtRef.current = 0;
  }, [studyInstanceUid, projectId]);
  useEffect(() => {
    const updatedAt = annotationsQuery.dataUpdatedAt;
    const action = decideSync({
      isSegmentationActive,
      updatedAt,
      lastSyncedUpdatedAt: lastSyncedDataUpdatedAtRef.current,
      hasAnnotations: !!annotationsQuery.data?.annotations,
    });
    if (action.kind === "skip") return;
    if (action.kind === "advance-only") {
      if (updatedAt) lastSyncedDataUpdatedAtRef.current = updatedAt;
      return;
    }
    if (loadSavedMasksByAnnotationId) {
      if (!predictionSegmentIndices) return;
      try {
        prepareLocalIndices();
      } catch (error) {
        logger.warn("[annotation index] allocation failed", error);
        emit("toast", { message: "Could not prepare saved mask indices. Please reload the case." });
        return;
      }
    }
    lastSyncedDataUpdatedAtRef.current = updatedAt!;

    const records: AnnotationRecord[] = annotationsQuery.data!.annotations.map(
      (a: AnnotationRecordResponse) => ({
        annotation_id: a.annotation_id,
        project_id: a.project_id,
        study_instance_uid: a.study_instance_uid,
        series_instance_uid: a.series_instance_uid,
        annotator: a.annotator,
        annotated_at: a.annotated_at,
        segment_index: a.segment_index,
        category: a.category,
        annotation_type: "segmentation" as const,
        mask_s3_path: a.mask_s3_path,
        bounding_box: a.bounding_box as any,
        segmentation_stats: a.segmentation_stats,
        hull_stats: a.hull_stats,
        measurements: {
          long_axis_mm: a.measurements.long_axis_mm ?? 0,
          short_axis_mm: a.measurements.short_axis_mm ?? 0,
        },
        attributes: a.attributes,
        timing: {
          start_ts: a.timing.start_ts ?? "",
          end_ts: a.timing.end_ts ?? "",
          duration_seconds: a.timing.duration_seconds ?? 0,
        },
        source: "cornerstone" as const,
      }),
    );
    setSavedAnnotations(records);
    if (records.length > 0) {
      // Exclude the organ's reserved index so it can't dominate the annotation max+1
      // (mirror the backend's non-organ sequencing).
      const lesionIndices = records
        .filter((r) => r.category !== "organ")
        .map((r) => r.segment_index);
      setActiveSegmentIndex(
        loadSavedMasksByAnnotationId
          ? localIndexMap.next()
          : lesionIndices.length > 0
            ? Math.max(...lesionIndices) + 1
            : 1,
      );
    }
  }, [
    annotationsQuery.data,
    annotationsQuery.dataUpdatedAt,
    isSegmentationActive,
    setSavedAnnotations,
    setActiveSegmentIndex,
    loadSavedMasksByAnnotationId,
    predictionSegmentIndices,
    prepareLocalIndices,
    localIndexMap,
  ]);

  // Load NIfTI mask as contour annotations — only on initial case open.
  // Skip if contours already exist (drawn this session or already loaded).
  const maskLoadedRef = useRef(false);
  const maskLoadRetriesRef = useRef(0);
  // Reset when study changes
  useEffect(() => {
    maskLoadedRef.current = false;
    maskLoadRetriesRef.current = 0;
  }, [studyInstanceUid, projectId]);

  // Mirror the Jotai `activeSegmentIndex` atom into the drawing tools'
  // module-level state. Without this, the contours each tool produces are
  // hard-coded to segment 1, so the 2nd stone in a multi-stone case is
  // stamped with the same `segmentIndex` as the 1st — the panel's save-button
  // gate and the save-payload extraction (both filter by
  // `segmentIndex === activeSegmentIndex`) then drop the new contour and the
  // Save button stays disabled. Cornerstone's own active-segment-index is
  // synced in `ensureContourSegmentationActive` (covers the lasso path).
  useEffect(() => {
    setRegionGrowSegmentIndex(activeSegmentIndex);
    setBumpSegmentIndex(activeSegmentIndex);
  }, [activeSegmentIndex]);

  // Apply a color to a set of segment indices across all viewports. Shared by
  // the annotation (purple/pink) and organ (blue) recolor effects.
  const recolorSegments = useCallback(
    (segIndices: number[], color: [number, number, number, number]) => {
      if (!segmentationVolumeId || segIndices.length === 0) return;
      const cs = getCornerstone();
      const ct = getCornerstoneTools();
      const contourSegId = getContourSegmentationId(segmentationVolumeId);
      const config = ct.segmentation?.config;
      if (!config) return;
      for (const engine of cs.getRenderingEngines?.() ?? []) {
        for (const vp of engine.getViewports?.() ?? []) {
          const vpId: string = (vp as any).id;
          for (const segIdx of segIndices) {
            setSegmentColor(config, vpId, contourSegId, segIdx, color);
          }
        }
        try {
          engine.render?.();
        } catch (err) {
          logger.debug("[SegmentationAnnotationPanel] recolor render skipped:", err);
        }
      }
    },
    [segmentationVolumeId],
  );

  const getLoadedSegmentationAnnotations = useCallback((): any[] => {
    const ct = getCornerstoneTools();
    const allAnns: any[] = ct.annotation?.state?.getAllAnnotations?.() ?? [];
    return allAnns.filter((ann) => {
      if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") return false;
      if (!isLoadedFromMask(ann)) return false;
      return ann?.data?.segmentation?.segmentIndex !== ORGAN_SEGMENT_INDEX;
    });
  }, []);

  // Every annotation is purple at rest. While drawing, only a pre-existing annotation
  // on the viewport's currently displayed slice turns pink; the new active
  // segment remains purple. Color is scoped per viewport so a same-index
  // contour on another visible slice does not flash pink.
  //
  // Source the segment indices to recolor from the actual `_loadedFromMask`
  // contour annotations on canvas — NOT from `savedAnnotations.segment_index`
  // filtered by `activeSegmentIndex`. The atom can be reset to maxIdx+1 by a
  // stale TanStack refetch, which collides with the saved index in
  // single-stone-saved cases and made the previous filter empty. Using the
  // canvas `_loadedFromMask` flag is independent of that race.
  useEffect(() => {
    if (!segmentationVolumeId) return;
    const loadedAnnotations = getLoadedSegmentationAnnotations();
    const loadedSegmentIndices = [
      ...new Set<number>(
        loadedAnnotations
          .map((ann) => ann?.data?.segmentation?.segmentIndex)
          .filter((idx): idx is number => typeof idx === "number"),
      ),
    ];
    if (loadedSegmentIndices.length === 0) return;
    const cs = getCornerstone();
    const ct = getCornerstoneTools();
    const contourSegId = getContourSegmentationId(segmentationVolumeId);
    const config = ct.segmentation?.config;
    if (!config) return;

    for (const engine of cs.getRenderingEngines?.() ?? []) {
      for (const viewport of engine.getViewports?.() ?? []) {
        const vp = viewport as any;
        const vpId: string = vp.id;
        // Establish the stable baseline first. This also removes any pink left
        // behind by a prior annotation session before the editor unmounted.
        for (const segIdx of loadedSegmentIndices) {
          setSegmentColor(config, vpId, contourSegId, segIdx, ACTIVE_SEGMENT_COLOR);
        }

        if (isSegmentationActive) {
          const imageIds: string[] = vp.getImageIds?.() ?? [];
          const currentIndex = vp.getCurrentImageIdIndex?.();
          const currentImageId =
            vp.getCurrentImageId?.() ??
            (typeof currentIndex === "number" ? imageIds[currentIndex] : undefined);
          if (currentImageId) {
            const sameSliceIndices = new Set<number>();
            for (const ann of loadedAnnotations) {
              const referencedImageId = ann?.metadata?.referencedImageId;
              if (
                typeof referencedImageId !== "string" ||
                (referencedImageId !== currentImageId &&
                  !referencedImageId.includes(currentImageId) &&
                  !currentImageId.includes(referencedImageId))
              ) {
                continue;
              }
              const idx = ann?.data?.segmentation?.segmentIndex;
              if (typeof idx === "number") sameSliceIndices.add(idx);
            }
            for (const segIdx of sameSliceIndices) {
              setSegmentColor(config, vpId, contourSegId, segIdx, SAVED_SEGMENT_COLOR);
            }
          }

          // The contour currently owned by the annotator is always purple,
          // even if a stale segment-index collision briefly occurs.
          setSegmentColor(config, vpId, contourSegId, activeSegmentIndex, ACTIVE_SEGMENT_COLOR);
        }
      }
      try {
        engine.render?.();
      } catch (err) {
        logger.debug("[SegmentationAnnotationPanel] slice color render skipped:", err);
      }
    }
  }, [
    isSegmentationActive,
    activeSegmentIndex,
    savedAnnotations,
    segmentationVolumeId,
    contourVersion,
    visibleSliceKey,
    getLoadedSegmentationAnnotations,
  ]);

  // Provide nnInteractive with project/study context only when the active
  // category's segmentation assist config has nn_interactive enabled.
  // Categories without this opt-in do not set AI context, so RegionGrowTool
  // falls through to the local flood-fill path.
  const nnEnabled = segAssistConfig?.region_grow?.nn_interactive === true;
  // Stone-only mode for the active category — drives nnInteractive to use the
  // stone-focusing seed bbox and tight volume guard, and RegionGrowTool to
  // enforce the bright HU floor. Everything else stays relaxed.
  const isStoneCategory = segAssistConfig?.is_stone === true;
  useEffect(() => {
    if (!studyInstanceUid || !nnEnabled) {
      setNnInteractiveContext(null);
      return;
    }
    setNnInteractiveContext({
      projectId,
      studyUid: studyInstanceUid,
      isStone: isStoneCategory,
    });
    return () => setNnInteractiveContext(null);
  }, [projectId, studyInstanceUid, nnEnabled, isStoneCategory]);

  useSavedMaskContourLoading({
    enabled: loadSavedMasksByAnnotationId,
    active: isSegmentationActive,
    ready: !!predictionSegmentIndices && annotationsQuery.isSuccess,
    scope: JSON.stringify([projectId, studyInstanceUid]),
    volumeId: segmentationVolumeId,
    data: annotationsQuery.data,
    revision: annotationsQuery.dataUpdatedAt,
    contourRevision: contourVersion,
    prepareIndices: () => {
      prepareLocalIndices();
      return new Map(
        (annotationsQuery.data?.annotations ?? []).map((ann) => [
          ann.segment_index,
          localIndexMap.resolve(ann.annotation_id),
        ]),
      );
    },
    onLoaded: () => setContourVersion((v) => v + 1),
  });

  useEffect(() => {
    // Other workflows retain their existing one-time load behavior.
    if (loadSavedMasksByAnnotationId) return;
    const maskUrl = annotationsQuery.data?.mask_url;
    if (!maskUrl || !segmentationVolumeId) return;
    if (maskLoadedRef.current) return; // already loaded or drawn this session
    const annotationIdsBySegment = new Map(
      (annotationsQuery.data?.annotations ?? []).map((annotation) => [
        annotation.segment_index,
        annotation.annotation_id,
      ]),
    );

    // Track every companion poll spawned inside this effect so we can cancel
    // them on unmount / case change instead of letting them tick for 10s.
    const companionPolls: Array<ReturnType<typeof setInterval>> = [];

    if (!loadSavedMasksByAnnotationId) {
      // Preserve the existing loading guard for other workflows.
      try {
        const csTools = getCornerstoneTools();
        const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
        const existingContours = allAnns.filter(
          (a: any) =>
            a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            a.data?.contour?.polyline?.length >= 3,
        );
        if (existingContours.length > 0) {
          maskLoadedRef.current = true;
          return; // contours already on canvas, skip mask loading
        }
      } catch (e) {
        logger.debug(
          "[SegmentationAnnotationPanel] contour check skipped (cornerstone not ready):",
          e,
        );
      }
    }

    // Poll for viewport readiness instead of fixed 2-second delay.
    // Check every 200ms for up to 10 seconds until a viewport with loaded
    // images and valid image data is found.
    let attempts = 0;
    const maxAttempts = 50; // 50 × 200ms = 10s max
    const annSeriesUid = savedAnnotations[0]?.series_instance_uid;

    const poll = setInterval(() => {
      attempts++;
      try {
        const cornerstone = getCornerstone();
        const engines: any[] = cornerstone.getRenderingEngines?.() ?? [];

        let matchedViewport: any = null;
        let matchedElement: HTMLElement | null = null;

        // Strategy 1: match by annotation's series UID
        if (annSeriesUid) {
          for (const engine of engines) {
            for (const vp of engine.getViewports?.() ?? []) {
              const ids: string[] = vp.getImageIds?.() ?? [];
              if (
                ids.length > 0 &&
                vp.getImageData?.()?.imageData &&
                (cornerstone.metaData.get("generalSeriesModule", ids[0])?.seriesInstanceUID ===
                  annSeriesUid.split("::")[0] ||
                  ids[0]?.includes(annSeriesUid.split("::")[0]))
              ) {
                matchedViewport = vp;
                matchedElement = vp.element ?? null;
                break;
              }
            }
            if (matchedViewport) break;
          }
        }

        // Keep the legacy fallback for callers without saved-mask identity.
        // Identity-based loading must wait for the annotation's own series.
        if (!matchedViewport) {
          for (const engine of engines) {
            for (const vp of engine.getViewports?.() ?? []) {
              if ((vp.getImageIds?.() ?? []).length > 0 && vp.getImageData?.()?.imageData) {
                matchedViewport = vp;
                matchedElement = vp.element ?? null;
                break;
              }
            }
            if (matchedViewport) break;
          }
        }

        if (!matchedViewport || !matchedElement) {
          if (attempts >= maxAttempts) {
            clearInterval(poll);
          }
          return; // not ready yet, try again
        }

        // Viewport ready — stop polling and load mask
        clearInterval(poll);
        const currentIdx = matchedViewport.getCurrentImageIdIndex?.() ?? 0;
        maskLoadedRef.current = true;

        loadMaskAsContours(maskUrl, segmentationVolumeId, matchedViewport, matchedElement, {
          annotationIdsBySegment,
          labelOrders: labelOrdersOf(annotationsQuery.data?.annotations ?? []),
        })
          .then(() => {
            try {
              matchedViewport.setImageIdIndex(currentIdx);
            } catch (e) {
              logger.debug("[MaskLoad] setImageIdIndex skipped:", e);
            }

            // Load companion plane masks (#217) after primary succeeds.
            // Each companion viewport may not be ready at the same instant the
            // primary is — sibling viewports mount on their own schedule, and
            // loadMaskAsContours throws if vtkImageData isn't populated. Poll
            // per-companion until the viewport has both its imageIds and its
            // VTK image data, then load.
            const companionUrls = annotationsQuery.data?.companion_mask_urls;
            const annotations = annotationsQuery.data?.annotations;
            if (!companionUrls || !annotations) return;

            const compSeriesMap = new Map<string, string>();
            for (const ann of annotations) {
              for (const comp of ann.companion_annotations ?? []) {
                compSeriesMap.set(comp.companion_id, comp.series_instance_uid);
              }
            }

            for (const [compId, compUrl] of Object.entries(companionUrls)) {
              const compSeriesUid = compSeriesMap.get(compId);
              if (!compSeriesUid) {
                console.warn(
                  `[MaskLoad] Companion ${compId} skipped: no series UID in annotation response`,
                );
                continue;
              }

              let compAttempts = 0;
              const compMaxAttempts = 50; // 50 × 200ms = 10s
              const compPoll = setInterval(() => {
                compAttempts++;
                const found = findViewportForSeries(compSeriesUid, getSiblingViewports() as any);
                const ready =
                  found &&
                  (found.viewport.getImageIds?.() ?? []).length > 0 &&
                  found.viewport.getImageData?.()?.imageData;

                if (!ready) {
                  if (compAttempts >= compMaxAttempts) {
                    clearInterval(compPoll);
                    console.warn(
                      `[MaskLoad] Companion ${compId} (series ${compSeriesUid}) viewport not ready after ${compMaxAttempts} attempts`,
                    );
                  }
                  return;
                }

                clearInterval(compPoll);
                const compViewport = found!.viewport;
                const compElement = found!.element;
                const compIdx = compViewport.getCurrentImageIdIndex?.() ?? 0;
                loadMaskAsContours(compUrl, segmentationVolumeId, compViewport, compElement, {
                  annotationIdsBySegment,
                  labelOrders: companionLabelOrders(annotations, compId),
                })
                  .then(() => {
                    try {
                      compViewport.setImageIdIndex(compIdx);
                    } catch (err) {
                      console.warn(`[MaskLoad] Companion ${compId} setImageIdIndex failed:`, err);
                    }
                    // #691: this companion's contours are now on canvas. Bump
                    // contourVersion so the loaded-mask recolor effect re-runs
                    // and applies the annotation color to these segment indices —
                    // otherwise the async companion segments keep Cornerstone's
                    // default colorLUT (segment 2 = green, etc.).
                    setContourVersion((v) => v + 1);
                  })
                  .catch((err: any) => {
                    console.warn(`[MaskLoad] Companion ${compId} load failed:`, err);
                  });
              }, 200);
              companionPolls.push(compPoll);
            }
          })
          .catch((err) => {
            maskLoadRetriesRef.current++;
            if (maskLoadRetriesRef.current < MAX_MASK_LOAD_RETRIES) {
              console.warn(
                `[MaskLoad] Failed (attempt ${maskLoadRetriesRef.current}/${MAX_MASK_LOAD_RETRIES}):`,
                err,
              );
              maskLoadedRef.current = false; // allow retry on next render
            } else {
              console.error(
                `[MaskLoad] Failed after ${MAX_MASK_LOAD_RETRIES} attempts, giving up:`,
                err,
              );
            }
          });
      } catch (e) {
        logger.debug("[SegmentationAnnotationPanel] viewport poll attempt failed:", e);
        if (attempts >= maxAttempts) clearInterval(poll);
      }
    }, 200);

    return () => {
      clearInterval(poll);
      for (const p of companionPolls) clearInterval(p);
    };
  }, [
    annotationsQuery.data?.mask_url,
    segmentationVolumeId,
    loadSavedMasksByAnnotationId,
    predictionSegmentIndices,
    localIndexMap,
    projectId,
    studyInstanceUid,
    isSegmentationActive,
  ]);

  // Local save state
  const isSaving = saveAnnotationMutation.isPending;
  const studyToolActions = useSegmentationToolActions({
    enabled: studyToolPolicy,
    saving: isSaving,
    engineAvailable: !compactStudyMode || allowAiSegmentation,
  });
  const setAnnotationSaveCount = useSetAtom(annotationSaveCountAtom);
  useEffect(() => {
    if (!isSaving) return;
    setAnnotationSaveCount((count) => count + 1);
    const contours = getCornerstoneTools()
      .annotation.state.getAllAnnotations()
      .filter(
        (annotation: any) => annotation.data?.segmentation?.segmentIndex === activeSegmentIndex,
      );
    const previousLocks = contours.map((annotation: any) => annotation.isLocked);
    contours.forEach((annotation: any) => {
      annotation.isLocked = true;
    });
    return () => {
      contours.forEach((annotation: any, index: number) => {
        if (!isLoadedFromMask(annotation)) annotation.isLocked = previousLocks[index];
      });
      setAnnotationSaveCount((count) => count - 1);
    };
  }, [isSaving, activeSegmentIndex, setAnnotationSaveCount]);

  // Local form state
  const [formValues, setFormValues] = useState<Record<string, AnnotationValue>>({});
  // Held in formValues so edit prefill and every reset cover it; Save sends the parsed integer.
  const readerConfidence = requireReaderConfidence
    ? parseReaderConfidence(formValues[READER_CONFIDENCE_KEY])
    : null;
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Derive schema from the selected category. `schemas` is gated to the
  // project's labels_to_assign (drives the Add-Annotation picker), but the
  // organ category is offered via the dedicated Segment Organ button (per
  // organ_policy) and is intentionally NOT in that picker — so resolve via the
  // full registry fallback to render the form for organ (and any other
  // policy-enabled category) instead of "not available in this project".
  const schema: SegmentationTypeSchema | null = selectedCategory
    ? resolveAnnotationSchema(schemas, selectedCategory)
    : null;
  const category = selectedCategory;

  // Compute per-category sequence numbers for display (e.g., "#1", "#2")
  const annotationSequenceLabels = useMemo(() => {
    const counters: Record<string, number> = {};
    const labels: Record<string, string> = {};
    for (const a of savedAnnotations) {
      counters[a.category] = (counters[a.category] ?? 0) + 1;
      labels[a.annotation_id] = `#${counters[a.category]}`;
    }
    return labels;
  }, [savedAnnotations]);

  // Count how many of the selected category already exist (for the "New" label)
  const newSequenceNumber = useMemo(() => {
    if (!selectedCategory) return 1;
    return savedAnnotations.filter((a) => a.category === selectedCategory).length + 1;
  }, [savedAnnotations, selectedCategory]);

  const handleFieldChange = useCallback((field: string, value: AnnotationValue) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Visible (non-hidden) schema keys — used to auto-select when only one option
  const visibleSchemaKeys = useMemo(
    () =>
      Object.entries(schemas)
        .filter(([, s]) => !s.hidden)
        // Organ is reached via the dedicated "Segment Organ" button, never the
        // annotation picker / auto-select — keep it out of this list.
        .filter(([k]) => k !== "organ")
        .map(([k]) => k),
    [schemas],
  );
  // Organ mask is universal but per-category: the button shows whenever any
  // assigned annotation category's organ_policy is "required" or "optional" (C2).
  // Legacy projects that list "organ" directly in labels_to_assign still work.
  // Computed from labels_to_assign — NOT `schemas`, which falls back to the full
  // ANNOTATION_SCHEMAS when labels are unset and would surface the button on
  // every unconfigured project.
  const organEnabled = organEnabledForLabels(labelsToAssign ?? []);

  // Annotation-triggered organ gate (C3): once ≥1 saved annotation of an
  // organ_policy="required" category exists and no organ mask is saved, the
  // "Segment Organ" step replaces "Mark Complete" until the organ is segmented.
  // optional/none categories never gate.
  const organGateActive = organGateActiveForSaved(savedAnnotations.map((a) => a.category));

  // Claim the next free segment index for a NEW annotation so it never reuses
  // the index handleSelect parked on the atom when a saved row was clicked.
  // Shared by BOTH start paths (Add-Annotation button + hotkey auto-start) so
  // they can't drift — the earlier fix patched only the button path while the
  // hotkey path kept lumping. savedAnnotations includes the optimistic post-save
  // record, so max+1 matches Save's onSuccess increment and can't collide with a
  // concurrent save (the race handleSelectCategory deliberately avoids — see
  // project_second_stone_lumping_bug).
  const claimNextSegmentIndex = useCallback(
    (isOrgan = false) => {
      if (loadSavedMasksByAnnotationId) {
        try {
          prepareLocalIndices();
          if (isOrgan && predictionSegmentIndices?.includes(ORGAN_SEGMENT_INDEX)) {
            throw new Error("Prediction index conflicts with the reserved organ index");
          }
          activateSegment(isOrgan ? ORGAN_SEGMENT_INDEX : localIndexMap.claim());
          return true;
        } catch (error) {
          logger.warn("[annotation index] allocation failed", error);
          emit("toast", { message: "Mask indices are not ready. Please wait or reload the case." });
          return false;
        }
      }
      // Organ owns a fixed reserved index (mirrors the backend). Annotations take the
      // next index over NON-organ segments only, so the organ's reserved value
      // can't dominate the max and push annotations to 101 — the "second stone lumps
      // with the first" collision (see project_second_stone_lumping_bug).
      if (isOrgan) {
        activateSegment(ORGAN_SEGMENT_INDEX);
        return true;
      }
      // #722 (data loss): derive the next index from the AUTHORITATIVE set —
      // live on-canvas contour segments (loaded-from-mask + drawn this session)
      // UNIONed with savedAnnotations — not stale savedAnnotations alone (the
      // annotations refetch is disabled, #241). A stale set picked an in-use
      // index and the backend overwrote the colliding stone (silent data loss).
      let liveIndices: number[] = [];
      try {
        liveIndices = collectAnnotationSegmentIndices(
          getCornerstoneTools().annotation?.state?.getAllAnnotations?.() ?? [],
        );
      } catch (err) {
        logger.debug("[claimNextSegmentIndex] live annotation read failed:", err);
      }
      const savedIndices = savedAnnotations
        .filter((a) => a.category !== "organ")
        .map((a) => a.segment_index);
      const used = [...new Set([...liveIndices, ...savedIndices])];
      activateSegment(nextAnnotationSegmentIndex(used));
      return true;
    },
    [
      savedAnnotations,
      activateSegment,
      loadSavedMasksByAnnotationId,
      prepareLocalIndices,
      localIndexMap,
      predictionSegmentIndices,
    ],
  );

  const handleAddNew = useCallback(() => {
    if (caseLocked || !claimNextSegmentIndex()) return;
    setFormValues({});
    setSelectedCategory(null);
    setIsAddingNew(true);
    onStartAnnotation?.();
    // Session entry point: mint a fresh undo scope so any leftover entry from a
    // prior (saved/cancelled) annotation is stale-scoped and can't be popped here.
    beginUndoSession(studyInstanceUid);
    setSelectedAnnotationId(null);
    // Auto-select if only one annotation type is available
    if (visibleSchemaKeys.length === 1) {
      // Defer so handleSelectCategory runs after state is set
      queueMicrotask(() => handleSelectCategoryRef.current?.(visibleSchemaKeys[0]));
    }
  }, [
    caseLocked,
    setSelectedAnnotationId,
    visibleSchemaKeys,
    studyInstanceUid,
    claimNextSegmentIndex,
    onStartAnnotation,
  ]);

  // Start an organ-mask annotation: pre-select the "organ" category with the
  // region-grow (nnInteractive) tool so the annotator's next click in the
  // parenchyma segments the whole organ. Mirrors the hotkey auto-start flow.
  const handleSegmentOrgan = useCallback(() => {
    if (!segmentationVolumeId || isDrawing) return;
    if (!claimNextSegmentIndex(true)) return; // organ → reserved index
    setFormValues({});
    setSelectedCategory(null);
    setIsAddingNew(true);
    onStartAnnotation?.();
    beginUndoSession(studyInstanceUid);
    setSelectedAnnotationId(null);
    queueMicrotask(() => handleSelectCategoryRef.current?.("organ", "regionGrow"));
  }, [
    segmentationVolumeId,
    isDrawing,
    studyInstanceUid,
    claimNextSegmentIndex,
    setSelectedAnnotationId,
    onStartAnnotation,
  ]);

  const handleSelectCategoryRef = useRef<
    ((cat: string, overrideTool?: MeasurementToolMode) => void) | null
  >(null);

  const handleSelectCategory = useCallback(
    (cat: string, overrideTool?: MeasurementToolMode) => {
      setSelectedCategory(cat);
      // Clear stale HU cache from any previous annotation
      clearSliceHUCache();
      // activeSegmentIndex is already the next available value — the sync
      // effect (`setActiveSegmentIndex(maxIdx + 1)` ~L659) seeds it from
      // max(saved)+1 on case open / refetch, and Save's onSuccess
      // (`setActiveSegmentIndex(activeSegmentIndex + 1)` ~L1470) increments
      // it after each save. Recomputing from savedAnnotations here would
      // race with the post-save refetch and collide two consecutive saves
      // onto the same index (lumping bug — see project_second_stone_lumping_bug).
      // Set segmentation assist config for this annotation type (null if not loaded)
      // Try exact match first, then snake_case (API keys are snake_case, labels may differ)
      const snakeCat = cat.toLowerCase().replace(/\s+/g, "_");
      const segAssist = segAssistQuery.data?.[cat] ?? segAssistQuery.data?.[snakeCat] ?? null;
      if (!segAssist) {
        // No silent fallback: a null config makes minSlicesToSave fall back to 2
        // (~L1946), which blocks single-slice (renal) saves — the "cannot hit
        // Save" report. Surface whether the seg-assist query simply hadn't
        // loaded yet (a race, cached after first load) vs. a genuine key mismatch
        // so it's diagnosable instead of silently greying out Save.
        logger.warn(
          "[SegmentationAnnotationPanel] no seg-assist config for category=%s (queryLoaded=%s) — Save uses the default min_slices_to_save; single-slice annotations may be blocked",
          cat,
          !!segAssistQuery.data,
        );
      }
      setSegmentationAssist(segAssist);
      // Set default bump radius if configured for this annotation type
      const defaultBumpIdx = segAssist?.workflow?.default_bump_radius;
      if (defaultBumpIdx != null && defaultBumpIdx >= 0 && defaultBumpIdx < BUMP_RADII.length) {
        setBumpRadius(BUMP_RADII[defaultBumpIdx]);
      }
      // Contour-rep setup (ensureContourSegmentationActive) is owned by
      // VolumeViewport — the panel only flips UI state. Keeps all viewport
      // gating in one place per the stack→volume migration plan.
      // Compact annotations default to Lasso unless the caller overrides it.
      // Starting-tool selection does not reset the user's segmentation engine
      // or restrict the other supported tools based on prediction origin.
      setIsSegmentationActive(true);
      const defaultTool = compactStudyMode
        ? "lasso"
        : segAssist?.region_grow?.enabled
          ? "regionGrow"
          : "lasso";
      setActiveMeasurementTool(overrideTool ?? defaultTool);
    },
    [
      setActiveMeasurementTool,
      setSegmentationAssist,
      segAssistQuery.data,
      setBumpRadius,
      clearSliceHUCache,
      setIsSegmentationActive,
      compactStudyMode,
    ],
  );

  // Keep ref in sync so handleAddNew can call it without a circular dependency
  handleSelectCategoryRef.current = handleSelectCategory;

  // Organ masks render blue (Tailwind blue-500), distinct from the purple/pink
  // annotation scheme. The organ owns the reserved index on both FE and backend, so
  // it's always at ORGAN_SEGMENT_INDEX whether actively drawn or reloaded.
  useEffect(() => {
    const hasOrgan =
      selectedCategory === "organ" || savedAnnotations.some((a) => a.category === "organ");
    if (!hasOrgan) return;
    recolorSegments([ORGAN_SEGMENT_INDEX], ORGAN_SEGMENT_COLOR);
  }, [selectedCategory, savedAnnotations, segmentationVolumeId, contourVersion, recolorSegments]);

  // Auto-start annotation when a segmentation hotkey is pressed without an active annotation.
  // The hotkey handler writes the desired tool to requestAnnotationStartAtom; we react by
  // creating a new annotation and then overriding the default tool with the requested one.
  const [requestedTool, setRequestedTool] = useAtom(requestAnnotationStartAtom);
  useEffect(() => {
    // Study entry belongs to Add/Edit. Never consume a stale legacy request.
    if (studyToolPolicy) {
      if (requestedTool) setRequestedTool(null);
      return;
    }
    if (!requestedTool || isSegmentationActive) {
      if (requestedTool) setRequestedTool(null);
      return;
    }
    // Block tool activation while viewport is still loading
    if (!segmentationVolumeId) {
      setRequestedTool(null);
      return;
    }
    // Trigger the same flow as clicking "Add Annotation", passing the
    // requested tool so handleSelectCategory uses it instead of the default.
    const toolToSet = requestedTool;
    setRequestedTool(null);
    if (!claimNextSegmentIndex()) return;
    setFormValues({});
    setSelectedCategory(null);
    setIsAddingNew(true);
    onStartAnnotation?.();
    // Same fresh-segment claim as handleAddNew — the hotkey auto-start path must
    // also not inherit the index handleSelect parked on the atom, else a new
    // annotation started by hotkey lumps onto the selected saved one.
    // Session entry point (hotkey auto-start): mint a fresh undo scope, same as
    // handleAddNew / handleEdit.
    beginUndoSession(studyInstanceUid);
    setSelectedAnnotationId(null);
    if (visibleSchemaKeys.length === 1) {
      queueMicrotask(() => handleSelectCategoryRef.current?.(visibleSchemaKeys[0], toolToSet));
    }
  }, [
    requestedTool,
    studyToolPolicy,
    isSegmentationActive,
    segmentationVolumeId,
    setRequestedTool,
    setSelectedAnnotationId,
    visibleSchemaKeys,
    studyInstanceUid,
    claimNextSegmentIndex,
    onStartAnnotation,
  ]);

  // Clear a segment's drawn pixels/contours from the Cornerstone canvas.
  // Zeros labelmap voxels (if the volume is cached) AND removes the matching
  // PlanarFreehand contour annotations from global state — both must happen
  // so the 3D viewer (which iterates cornerstone annotation state) doesn't
  // keep showing the just-cleared segment.
  const clearSegmentFromCanvas = useCallback(
    (segmentIndex: number) => {
      try {
        const cornerstone = getCornerstone();

        // 1. Labelmap voxels: zero out everywhere this segment was painted.
        if (segmentationVolumeId) {
          const volume = cornerstone.cache.getVolume(segmentationVolumeId) as any;
          const scalarData: Uint8Array | undefined =
            typeof volume?.getScalarData === "function"
              ? volume.getScalarData()
              : volume?.scalarData;
          if (scalarData) {
            for (let i = 0; i < scalarData.length; i++) {
              if (scalarData[i] === segmentIndex) scalarData[i] = 0;
            }
          }
        }

        // 2. Contour annotations: drop every PlanarFreehand for this segment.
        // Match by segmentIndex only (segmentationId varies by viewport).
        const cornerstoneTools = getCornerstoneTools();
        const annotationState = cornerstoneTools.annotation.state;
        const allAnnotations = annotationState.getAllAnnotations() as any[];
        for (const ann of allAnnotations) {
          if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
          // AI prediction contours are immutable source annotations. Their segment
          // indices can overlap a newly claimed user segment while Cornerstone is
          // switching the active segmentation, so cancellation must never remove
          // them as part of the in-progress annotation cleanup.
          if (
            ann?._predictionMask === true &&
            !(loadSavedMasksByAnnotationId && ann.data?.savedAnnotationId)
          )
            continue;
          if (ann?.data?.segmentation?.segmentIndex !== segmentIndex) continue;
          try {
            annotationState.removeAnnotation(ann.annotationUID);
          } catch (err) {
            logger.debug("[clearSegmentFromCanvas] removeAnnotation failed:", err);
          }
        }

        // 3. Force re-render so the labelmap update and contour removal show up.
        cornerstone.getRenderingEngines?.()?.forEach((e: any) => {
          try {
            e.render();
          } catch (err) {
            logger.debug("[clearSegmentFromCanvas] render failed:", err);
          }
        });
      } catch (err) {
        logger.debug("[clearSegmentFromCanvas] canvas clear failed:", err);
      }
    },
    [segmentationVolumeId, loadSavedMasksByAnnotationId],
  );

  // Lock a segment's drawn contours in place so they render in the same
  // "saved mask" style as contours rehydrated by niftiMaskLoader on reload.
  // Used after a successful save: the contours that were just drawn ARE the
  // saved mask, so we keep them on canvas and flip the flags that the
  // contour renderer reads (isLocked + _loadedFromMask) instead of erasing
  // and waiting for a refetch that doesn't happen (#241).
  const lockSegmentOnCanvas = useCallback((segmentIndex: number, savedAnnotationId?: string) => {
    try {
      const cornerstoneTools = getCornerstoneTools();
      const annotationState = cornerstoneTools.annotation.state;
      const allAnnotations = annotationState.getAllAnnotations() as any[];
      for (const ann of allAnnotations) {
        if (ann?.metadata?.toolName !== "PlanarFreehandContourSegmentationTool") continue;
        if (ann?.data?.segmentation?.segmentIndex !== segmentIndex) continue;
        if (savedAnnotationId) ann.data.savedAnnotationId = savedAnnotationId;
        ann.isLocked = true;
        ann.highlighted = false;
        setLoadedFromMask(ann, true);
      }
      getCornerstone()
        .getRenderingEngines?.()
        ?.forEach((e: any) => {
          try {
            e.render();
          } catch (err) {
            logger.debug("[lockSegmentOnCanvas] render failed:", err);
          }
        });
    } catch (err) {
      logger.debug("[lockSegmentOnCanvas] failed:", err);
    }
  }, []);

  /** Returns whether the editor closed; false leaves the edit open for retry. */
  const handleCancel = useCallback((): boolean => {
    if (isSaving || getNnRequestInFlight()) {
      emit("toast", {
        message: "Please wait for the current operation to finish before cancelling.",
      });
      return false;
    }
    // Prediction edits restore their checkpoint; clearing afterward would erase it.
    if (predictionEditSegmentIndex != null) {
      // Entry may not have happened yet (ENG-186 step 3): nothing to restore, so
      // just close. A partial entry (checkpoint taken, completion missing) still
      // restores first.
      const completion =
        predictionEditCompletionRef.current ??
        getPredictionEditCompletion?.(predictionEditSegmentIndex) ??
        null;
      if (completion && !completion.cancel()) return false;
      predictionEditCompletionRef.current = null;
      endNnSession();
      setNnSegResult(null);
    } else if (savedEditCheckpoint.current) {
      const checkpoint = savedEditCheckpoint.current;
      if (
        checkpoint.study !== studyInstanceUid ||
        checkpoint.snapshot.segmentationVolumeId !== segmentationVolumeId
      ) {
        emit("toast", {
          message:
            "Cannot restore this edit after the image volume changed. Please reopen the case.",
        });
        return false;
      }
      try {
        restorePredictionMaskEditSnapshot(createPredictionMaskOverlayState(), checkpoint.snapshot);
      } catch {
        emit("toast", { message: "Could not restore the saved mask. Please retry Cancel." });
        return false;
      }
      savedEditCheckpoint.current = null;
    } else if (isDrawing) {
      clearSegmentFromCanvas(activeSegmentIndex);
    }
    // Hygiene: drop this cancelled session's undo entries. (Correctness is
    // already covered — the scope key makes them inert once the next session
    // begins, and popUndo is null-scoped while segmentation is inactive.)
    clearUndo();
    editingAnnotationIdRef.current = null;
    setIsAddingNew(false);
    setFormValues({});
    setSelectedCategory(null);
    setActiveMeasurementTool("none");
    setIsDrawing(false);
    setIsSegmentationActive(false);
    setAnnotationStartTs(null);
    setLockedSeriesId(null);
    setHasFirstStroke(false);
    setDerivedMeasurements(null);
    setNnClickMarkers([]);
    clearSliceHUCache();
    // Restore synchronously so there is no pink frame between cancellation and
    // the inactive-state effect repainting every saved/AI contour purple.
    const loadedIndices = [
      ...new Set<number>(
        getLoadedSegmentationAnnotations()
          .map((ann) => ann?.data?.segmentation?.segmentIndex)
          .filter((idx): idx is number => typeof idx === "number"),
      ),
    ];
    recolorSegments(loadedIndices, ACTIVE_SEGMENT_COLOR);
    onCancelAnnotation?.();
    return true;
  }, [
    isSaving,
    isDrawing,
    activeSegmentIndex,
    clearSegmentFromCanvas,
    setActiveMeasurementTool,
    setIsDrawing,
    setIsSegmentationActive,
    setAnnotationStartTs,
    setDerivedMeasurements,
    setNnClickMarkers,
    getLoadedSegmentationAnnotations,
    recolorSegments,
    onCancelAnnotation,
    isSaving,
    predictionEditSegmentIndex,
    getPredictionEditCompletion,
    setNnSegResult,
    studyInstanceUid,
    segmentationVolumeId,
  ]);

  /**
   * Resolves true once the backend accepted the save, false when nothing was
   * sent or the request failed. Failures are already reported to the user here.
   */
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (isSaving || !annotationStartTs || !category) return false;
    // Hotkey and card-Accept saves bypass the disabled button, so gate here too.
    if (requireReaderConfidence && readerConfidence == null) {
      emit("toast", { message: `${READER_CONFIDENCE_HINT} before saving.` });
      return false;
    }

    const startTs = annotationStartTs; // capture before async mutation
    const endTs = new Date().toISOString();
    // Idle-excluded active time (>60s gaps + tab-hidden spans removed). Captured
    // before the async mutation so it reflects the draw window, not save latency.
    const activeSeconds = getActiveSeconds(endTs);

    // Extract sparse labelmap from Cornerstone segmentation volume
    const labelmap: Record<string, number[][]> = {};
    let volumeShape: [number, number, number] = [0, 0, 0];
    let spacing: [number, number, number] = [1, 1, 1];
    let primaryOrigin: [number, number, number] | undefined;
    let primaryDirection: number[] | undefined;
    let extractedSeriesUid: string | undefined;
    let manualSlices = new Set<number>();
    let interpolatedSlices = new Set<number>();
    let bumpedSlices = new Set<number>();
    let regionGrowSlices = new Set<number>();
    let pendingCompanions: {
      result: NonNullable<ReturnType<typeof buildLabelmapFromContours>>;
      seriesUid: string;
    }[] = [];

    try {
      const cornerstone = getCornerstone();
      const volume = cornerstone.cache.getVolume(segmentationVolumeId!) as any;

      // Check for contour annotations first — V1 uses PlanarFreehandContourSegmentationTool
      // which stores contours as annotations, NOT in the labelmap scalar data.
      const cornerstoneTools = getCornerstoneTools();
      const annotationState = cornerstoneTools.annotation.state;
      const allAnnotations: any[] = annotationState.getAllAnnotations() ?? [];

      // Match by segmentIndex only — don't filter by segmentationId because
      // each viewport creates its own segmentation ID. The annotation might have
      // been drawn on a different viewport than the one that set segmentationVolumeId.
      // Skip contours loaded from saved masks (_loadedFromMask) to avoid re-saving them.
      const segContours = allAnnotations.filter(
        (a: any) =>
          a.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          a.data?.segmentation?.segmentIndex === activeSegmentIndex &&
          a.data?.contour?.polyline?.length >= 3 &&
          !isLoadedFromMask(a),
      );

      if (volume && segContours.length === 0) {
        // ── VolumeViewport labelmap path: only when no contours exist (V2/V3 brush tools) ──
        const scalarData: Uint8Array =
          typeof volume.getScalarData === "function" ? volume.getScalarData() : volume.scalarData;
        const [cols, rows, slices] = volume.dimensions as [number, number, number];
        volumeShape = [rows, cols, slices];
        const voxelSpacing = volume.spacing as [number, number, number];
        spacing = [voxelSpacing[1], voxelSpacing[0], voxelSpacing[2]];
        primaryOrigin = volume.origin
          ? ([...volume.origin] as [number, number, number])
          : undefined;
        primaryDirection = volume.direction ? Array.from(volume.direction) : undefined;

        for (let i = 0; i < scalarData.length; i++) {
          if (scalarData[i] === activeSegmentIndex) {
            const sliceIdx = Math.floor(i / (cols * rows));
            const remainder = i % (cols * rows);
            const row = Math.floor(remainder / cols);
            const col = remainder % cols;
            const key = String(sliceIdx);
            if (!labelmap[key]) labelmap[key] = [];
            labelmap[key].push([row, col]);
          }
        }
      } else {
        // ── Contour extraction path: V1 lasso tool / nnInteractive ──
        // Group contours by owning viewport/series so we can save each plane separately.
        const siblings = getSiblingViewports();
        const engines = cornerstone.getRenderingEngines?.() ?? [];

        // Group contours by viewport ID
        const contourGroups = new Map<string, { contours: any[]; seriesUid: string }>();
        for (const ann of segContours) {
          const imageId = ann.metadata?.referencedImageId ?? "";
          let matched = false;
          // Check sibling viewports first (preferred — gives us series UIDs)
          if (siblings) {
            for (const [, sib] of Object.entries(siblings)) {
              const vpIds: string[] = sib.viewport?.getImageIds?.() ?? [];
              if (vpIds.includes(imageId)) {
                const key = sib.seriesUid ?? sib.viewport?.id ?? "unknown";
                if (!contourGroups.has(key)) {
                  contourGroups.set(key, { contours: [], seriesUid: sib.seriesUid ?? "" });
                }
                contourGroups.get(key)!.contours.push(ann);
                matched = true;
                break;
              }
            }
          }
          // Fallback: scan all viewports
          if (!matched) {
            for (const engine of engines) {
              for (const vp of engine.getViewports?.() ?? []) {
                const vpIds: string[] = (vp as any).getImageIds?.() ?? [];
                if (vpIds.includes(imageId)) {
                  const key = vp.id as string;
                  if (!contourGroups.has(key)) {
                    const ownerSeriesUid = cornerstone.metaData.get(
                      "generalSeriesModule",
                      imageId,
                    )?.seriesInstanceUID;
                    if (!ownerSeriesUid) {
                      emit("toast", {
                        message:
                          "Cannot identify the contour's source series. Please reopen the case before saving.",
                      });
                      return false;
                    }
                    contourGroups.set(key, { contours: [], seriesUid: ownerSeriesUid });
                  }
                  contourGroups.get(key)!.contours.push(ann);
                  matched = true;
                  break;
                }
              }
              if (matched) break;
            }
          }
        }

        // Prefer the panel's series, but keep the extracted geometry and its owner together.
        const primarySeriesUid = seriesInstanceUid?.split("::")[0] ?? "";
        let primaryResult: ReturnType<typeof buildLabelmapFromContours> = null;
        const companionResults: {
          result: NonNullable<ReturnType<typeof buildLabelmapFromContours>>;
          seriesUid: string;
        }[] = [];

        for (const group of Array.from(contourGroups.values())) {
          const result = buildLabelmapFromContours(
            group.contours,
            cornerstone,
            getBumpedImageIds,
            getRegionGrowImageIds,
          );
          if (!result) continue;
          const cleanUid = group.seriesUid.split("::")[0];
          if (cleanUid === primarySeriesUid) {
            primaryResult = result;
            extractedSeriesUid = cleanUid;
          } else {
            companionResults.push({ result, seriesUid: cleanUid });
          }
        }

        if (!primaryResult) {
          // Promote the first successfully extracted group, removing it from companions.
          const promoted = companionResults.shift();
          if (promoted) {
            primaryResult = promoted.result;
            extractedSeriesUid = promoted.seriesUid;
          }
        }

        if (primaryResult) {
          Object.assign(labelmap, primaryResult.labelmap);
          volumeShape = primaryResult.volumeShape;
          spacing = primaryResult.spacing;
          primaryOrigin = primaryResult.origin;
          primaryDirection = primaryResult.direction;
          manualSlices = primaryResult.manualSlices;
          interpolatedSlices = primaryResult.interpolatedSlices;
          bumpedSlices = primaryResult.bumpedSlices;
          regionGrowSlices = primaryResult.regionGrowSlices;
        }

        // ENG-280: the segmentation study saves one record per stone. Keep the
        // primary (or the promoted group) and drop contours on other series.
        if (companionMasksBlocked && companionResults.length > 0) {
          logger.warn("[Save] companions_dropped", {
            count: companionResults.length,
            primarySeriesUid: extractedSeriesUid,
          });
          companionResults.length = 0;
        }

        // companionResults is used after primary save to fire companion saves
        pendingCompanions = companionResults;
      }
    } catch (err) {
      if (err instanceof StackGeometryError) {
        // 2D-only (stack) viewing and the series geometry can't be confirmed:
        // refuse rather than write a wrong mask header (ENG-258).
        logger.warn("[Save] stack_geometry_unconfirmed", { reason: err.message });
        emit("toast", {
          message:
            "Couldn't save this stone: slice positions for this series can't be confirmed in 2D-only viewing. Reload the page (it may switch to 3D viewing) and draw it again. If this keeps happening, tell the study team.",
        });
        return false;
      }
      console.error("[Save] Extraction error:", err);
    }

    if (Object.keys(labelmap).length === 0) {
      console.error(
        "[Save] No segmentation data extracted — aborting save. Re-draw the annotation and try again.",
      );
      return false;
    }

    // studyInstanceUid may be a composite key like "1.2.826...::reg-ecr-demo-174-..."
    // The backend expects a pure DICOM UID (digits and dots only).
    const cleanStudyUid = studyInstanceUid.split("::")[0];
    if (!seriesInstanceUid) {
      throw new Error("seriesInstanceUid is required to save an annotation");
    }
    const cleanSeriesUid = extractedSeriesUid ?? seriesInstanceUid.split("::")[0];

    const totalSlices = Object.keys(labelmap).length;

    // Bundle companion labelmaps into the primary payload (#217)
    const companionLabelmaps: CompanionLabelmapInput[] = pendingCompanions.map(
      ({ result: compResult, seriesUid: compSeriesUid }) => ({
        series_instance_uid: compSeriesUid,
        labelmap: compResult.labelmap,
        volume_shape: compResult.volumeShape,
        spacing: compResult.spacing,
        volume_origin: compResult.origin,
        volume_direction: compResult.direction,
        contour_stats: {
          total_slices: Object.keys(compResult.labelmap).length,
          manual_slices: compResult.manualSlices.size,
          interpolated_slices: compResult.interpolatedSlices.size,
          bumped_slices: compResult.bumpedSlices.size,
          region_grow_slices: compResult.regionGrowSlices.size,
          source_series_uid: cleanSeriesUid,
        },
      }),
    );

    // Build hull stats from 3D viewer atoms (#217).
    // Sort defensively: axis_1_mm must be longest, axis_3_mm shortest.
    const hullStatsPayload: HullStatsInput | undefined =
      hullVolume != null && hullAxes?.length === 3
        ? (() => {
            const sorted = [...hullAxes].sort((a, b) => b.length - a.length);
            return {
              volume_mm3: hullVolume,
              axis_1_mm: sorted[0].length,
              axis_2_mm: sorted[1].length,
              axis_3_mm: sorted[2].length,
            };
          })()
        : undefined;

    const editedId = editingAnnotationIdRef.current;
    const payload: AnnotationInput = {
      edited_annotation_id: editedId && !editedId.startsWith("local_") ? editedId : undefined,
      project_id: projectId,
      study_instance_uid: cleanStudyUid,
      series_instance_uid: cleanSeriesUid,
      category,
      labelmap,
      measurements: {
        long_axis_mm: derivedMeasurements?.long_axis_mm ?? 0,
        short_axis_mm: derivedMeasurements?.short_axis_mm ?? 0,
      },
      hu_stats: {
        hu_min: derivedMeasurements?.hu_min ?? 0,
        hu_max: derivedMeasurements?.hu_max ?? 0,
        hu_avg: derivedMeasurements?.hu_avg ?? 0,
      },
      attributes: {
        ...formValues,
        ...annotationMetadata,
        // Reaching Save on a prediction edit means its geometry changed (ENG-277).
        ...(annotationMetadata?.ai_prediction_condition
          ? { ai_prediction_review: "modified" }
          : {}),
        ...(readerConfidence != null ? { [READER_CONFIDENCE_KEY]: readerConfidence } : {}),
        ...(loadSavedMasksByAnnotationId &&
        !modelInitialized &&
        !annotationMetadata?.ai_prediction_condition &&
        !formValues.ai_prediction_condition
          ? { manual_annotation_review: "accepted" }
          : {}),
      },
      timing: { start_ts: startTs, end_ts: endTs, active_seconds: activeSeconds },
      time_event: annotationSaveStamp() ?? undefined,
      contour_stats: {
        total_slices: totalSlices,
        manual_slices: manualSlices.size,
        interpolated_slices: interpolatedSlices.size,
        bumped_slices: bumpedSlices.size,
        region_grow_slices: regionGrowSlices.size,
        source_series_uid: cleanSeriesUid,
      },
      volume_shape: volumeShape,
      spacing,
      volume_origin: primaryOrigin,
      volume_direction: primaryDirection,
      companion_labelmaps: companionLabelmaps.length > 0 ? companionLabelmaps : undefined,
      hull_stats: hullStatsPayload,
    };

    const saveContext = {
      completion: predictionEditCompletionRef.current,
      editedId,
    };
    setActiveMeasurementTool("none");
    let settle!: (saved: boolean) => void;
    const settled = new Promise<boolean>((resolve) => (settle = resolve));
    saveAnnotationMutation.mutate(payload, {
      onSuccess: (data) => {
        // The backend has the annotation even if this view went stale below.
        settle(true);
        markAnnotationSaved();
        if (loadSavedMasksByAnnotationId && currentLocalIndexMap.current !== localIndexMap) return;
        const { completion } = saveContext;
        if (completion) {
          if (predictionEditCompletionRef.current !== completion) return;
          if (!completion.commit()) return;
        }
        // The backend updates the existing annotation; keep its edited contours.
        if (loadSavedMasksByAnnotationId)
          localIndexMap.bind(data.annotation_id, activeSegmentIndex);
        lockSegmentOnCanvas(activeSegmentIndex, data.annotation_id);
        predictionEditCompletionRef.current = null;
        savedEditCheckpoint.current = null;
        editingAnnotationIdRef.current = null;

        // Optimistically add the new annotation to the panel immediately,
        // so it appears without waiting for the query refetch (#155 panel bug).
        const optimisticAnnotation: AnnotationRecord = {
          annotation_id: data.annotation_id,
          project_id: projectId,
          study_instance_uid: studyInstanceUid.split("::")[0],
          series_instance_uid: cleanSeriesUid,
          annotator: "me", // placeholder until query refetch
          annotated_at: new Date().toISOString(),
          segment_index: data.segment_index,
          category: category!,
          annotation_type: "segmentation",
          mask_s3_path: "", // populated by query refetch
          // bounding_box omitted — optional field, populated by query refetch
          segmentation_stats: data.segmentation_stats,
          hull_stats: data.hull_stats ?? null,
          measurements: {
            long_axis_mm: derivedMeasurements?.long_axis_mm ?? 0,
            short_axis_mm: derivedMeasurements?.short_axis_mm ?? 0,
          },
          attributes: payload.attributes,
          timing: {
            start_ts: startTs,
            end_ts: endTs,
            // Backend stores active_seconds as duration_seconds; mirror that
            // here so the panel shows the idle-corrected time immediately.
            duration_seconds: activeSeconds,
            active_seconds: activeSeconds,
          },
          source: "cornerstone",
        };
        setSavedAnnotations((prev) => [
          // Organ is a per-annotator singleton at a reserved index — a correction
          // replaces the prior organ row rather than appending a duplicate (the
          // backend reuses the same row; #241 means the query isn't invalidated on
          // save, so without this the panel would show two organ rows until reload).
          ...prev.filter(
            (a) =>
              a.annotation_id !== (editedId ?? data.annotation_id) &&
              (category !== "organ" || a.category !== "organ"),
          ),
          optimisticAnnotation,
        ]);

        // Reset drawing state for next annotation
        resetBumpTracking();
        // Drop the saved stone's region-grow preview (UID, seeds, cached
        // slice). Left behind, the next Shift+Click re-grew from the old seed
        // and replaced the saved contour as part of the new stone (ENG-250).
        resetRegionGrowState();
        resetRegionGrowTracking();
        clearUndo(); // annotation undo log is per-annotation; saved → fresh
        clearSliceHUCache();
        setAnnotationStartTs(null);
        setIsDrawing(false);
        setDerivedMeasurements(null);
        setLockedSeriesId(null);
        setHasFirstStroke(false);
        setIsSegmentationActive(false);
        setNnClickMarkers([]);

        // Drop the nnInteractive session bookkeeping now that the contours
        // are committed/locked. Without this, the stale _multiSessions entries
        // either (a) crash on the next AI/region-grow click because their
        // viewport refs point at evicted volumes after case nav, or (b) reuse
        // the previous stone's lastResponse/segmentIndex and lump it together
        // with the new annotation. Cornerstone annotations are left alone —
        // lockSegmentOnCanvas above owns their lifecycle from here.
        endNnSession();
        // nnSegmentationResult gates SegmentationViewerPanel's choice between
        // the 3D scene and the empty-state stats panel; clearing it reverts to
        // the initial screen after save.
        setNnSegResult(null);
        setActiveSegmentIndex(
          loadSavedMasksByAnnotationId ? localIndexMap.next() : activeSegmentIndex + 1,
        );

        // Reset form
        setIsAddingNew(false);
        setFormValues({});
        setSelectedCategory(null);
        setActiveMeasurementTool("none");
        setContourVersion((v) => v + 1);
        onAnnotationSaved?.(optimisticAnnotation);
      },
      onError: (error: any) => {
        settle(false);
        console.error(
          "[Save] Mutation failed:",
          error?.response?.status,
          error?.response?.data?.detail,
        );
        const { completion } = saveContext;
        if (completion) {
          if (predictionEditCompletionRef.current !== completion) return;
          // Read-only validation: never commit or cancel a failed Save.
          if (
            predictionEditSegmentIndex == null ||
            !getPredictionEditCompletion?.(predictionEditSegmentIndex)
          )
            return;
        }
        emit("toast", {
          message:
            error?.response?.status === 409
              ? "Saved decisions changed. Cancel and reopen this candidate editor before saving."
              : "Could not save the annotation. Your edits are still available. Please try again.",
          durationMs: 8000,
        });
      },
    });

    // Companions are now bundled in the primary payload (#217).
    // No separate companion saves needed.
    return settled;
  }, [
    isSaving,
    annotationStartTs,
    getActiveSeconds,
    annotationSaveStamp,
    markAnnotationSaved,
    derivedMeasurements,
    activeSegmentIndex,
    segmentationVolumeId,
    projectId,
    studyInstanceUid,
    seriesInstanceUid,
    category,
    formValues,
    annotationMetadata,
    requireReaderConfidence,
    readerConfidence,
    modelInitialized,
    saveAnnotationMutation,
    setAnnotationStartTs,
    setIsDrawing,
    setDerivedMeasurements,
    setActiveSegmentIndex,
    setActiveMeasurementTool,
    instanceToSliceMap,
    setSavedAnnotations,
    setIsSegmentationActive,
    setLockedSeriesId,
    setHasFirstStroke,
    hullVolume,
    hullAxes,
    companionMasksBlocked,
    setNnClickMarkers,
    lockSegmentOnCanvas,
    setNnSegResult,
    onAnnotationSaved,
    getPredictionEditCompletion,
    predictionEditSegmentIndex,
    loadSavedMasksByAnnotationId,
    localIndexMap,
  ]);

  // Save on a prediction edit checks the geometry first, as Accept does (ENG-277):
  // a confidence-only change must not re-rasterize the mask or mark it modified.
  const unchangedSaveInFlight = useRef(false);
  const [unchangedSaving, setUnchangedSaving] = useState(false);
  const handleEditorSave = useCallback(async (): Promise<boolean> => {
    if (predictionEditSegmentIndex == null || !onUnchangedPredictionSave) return handleSave();
    if (isSaving || unchangedSaveInFlight.current) return false;
    if (requireReaderConfidence && readerConfidence == null) {
      emit("toast", { message: `${READER_CONFIDENCE_HINT} before saving.` });
      return false;
    }
    // No completion means entry never happened (ENG-186 step 3): nothing was edited.
    const completion = predictionEditCompletionRef.current;
    let changed: boolean;
    try {
      changed = !!completion && completion.isModified();
    } catch (error) {
      logger.warn("[Save] prediction edit checkpoint is stale; not saving", error);
      emit("toast", { message: "Could not check your changes. Please reopen the case." });
      return false;
    }
    if (changed) return handleSave();

    unchangedSaveInFlight.current = true;
    setUnchangedSaving(true);
    try {
      return (await onUnchangedPredictionSave(readerConfidence)) === "closed";
    } finally {
      unchangedSaveInFlight.current = false;
      setUnchangedSaving(false);
    }
  }, [
    predictionEditSegmentIndex,
    onUnchangedPredictionSave,
    handleSave,
    isSaving,
    requireReaderConfidence,
    readerConfidence,
  ]);

  const [saveRequest, setSaveRequest] = useAtom(saveAnnotationRequestAtom);
  useEffect(() => {
    if (saveRequest > 0) {
      setSaveRequest(0);
      void handleEditorSave();
    }
  }, [saveRequest, setSaveRequest, handleEditorSave]);

  // Card verdicts on the candidate being edited must not drop unsaved edits (ENG-185).
  useEffect(() => {
    if (!predictionEditActionsRef || predictionEditSegmentIndex == null) return;
    predictionEditActionsRef.current = {
      readerConfidence,
      accept: async () => {
        const completion = predictionEditCompletionRef.current;
        // Entry never happened (ENG-186 step 3): nothing was edited, so close and
        // let CaseViewer accept the AI mask as unchanged.
        if (!completion) return handleCancel() ? "unchanged" : "failed";
        if (!completion.isModified()) return handleCancel() ? "unchanged" : "failed";
        return (await handleSave()) ? "saved" : "failed";
      },
      discard: handleCancel,
    };
    return () => {
      predictionEditActionsRef.current = null;
    };
  }, [
    predictionEditActionsRef,
    predictionEditSegmentIndex,
    handleCancel,
    handleSave,
    readerConfidence,
  ]);

  const handleDelete = useCallback(
    (annotationId: string) => {
      const annotation = savedAnnotations.find((a) => a.annotation_id === annotationId);
      if (annotation) {
        const localIndex = resolveAnnotationLocalIndex(annotation);
        if (localIndex == null) return;
        clearSegmentFromCanvas(localIndex);
      }

      // Optimistically remove from local state
      setSavedAnnotations((prev) => prev.filter((a) => a.annotation_id !== annotationId));
      if (selectedAnnotationId === annotationId) {
        setSelectedAnnotationId(null);
      }

      // Reset viewport lock for next annotation
      setLockedSeriesId(null);
      setHasFirstStroke(false);
      setIsSegmentationActive(false);
      setActiveMeasurementTool("none");

      // Clear region-grow / nnInteractive module state so the next click
      // doesn't inherit the deleted stone's preview UID, cached fill range,
      // nn sessions, or oversized-strike count. Without this, a small-stone
      // delete leaves stale per-plane sessions and a stale `_cachedContext`
      // that bypass the fresh FWHM bounds on the next click — the user sees
      // the kidney engulfed again even though the math is correct.
      resetRegionGrowState();
      resetRegionGrowTracking();
      clearUndo(); // annotation removed → its in-session undo entries are stale

      // Delete from backend (skip for local-only annotations that were never persisted)
      if (!annotationId.startsWith("local_")) {
        deleteAnnotationMutation.mutate(annotationId);
      }

      // Clear 3D viewer and click markers — contours changed outside nnInteractive flow
      setNnSegResult(null);
      setNnClickMarkers([]);
      setContourVersion((v) => v + 1);
    },
    [
      savedAnnotations,
      clearSegmentFromCanvas,
      resolveAnnotationLocalIndex,
      setSavedAnnotations,
      selectedAnnotationId,
      setSelectedAnnotationId,
      deleteAnnotationMutation,
      setNnClickMarkers,
    ],
  );

  const handleEdit = useCallback(
    (annotation: AnnotationRecord) => {
      if (isDrawing || caseLocked) return;
      const localIndex = resolveAnnotationLocalIndex(annotation);
      if (localIndex == null) return;
      const condition = annotation.attributes.ai_prediction_condition;
      const source = annotation.attributes.ai_prediction_source_segment_index;
      if (
        typeof condition === "string" &&
        typeof source === "number" &&
        onCandidateEditStart &&
        !onCandidateEditStart(condition, source)
      )
        return;

      // #220: Verify the annotation's series is loaded in a current viewport
      try {
        const targetUid = annotation.series_instance_uid;
        if (targetUid && !findViewportForSeries(targetUid, getSiblingViewports() as any)) {
          emit("toast", {
            message: "Cannot edit: annotation series is not loaded in the current hanging protocol",
          });
          return;
        }
      } catch {
        /* cornerstone not ready — allow edit to proceed */
      }

      // Unlock the loaded mask contours for this segment so they become
      // editable and are included in the save filter (which skips _loadedFromMask).
      if (savedEditRequest?.annotationId === annotation.annotation_id) {
        try {
          if (!segmentationVolumeId) return;
          savedEditCheckpoint.current = {
            study: studyInstanceUid,
            snapshot: capturePredictionMaskEditSnapshot(
              createPredictionMaskOverlayState(),
              segmentationVolumeId,
              localIndex,
            ),
          };
        } catch {
          emit("toast", {
            message: "Saved mask is not ready for editing. Please reopen the case.",
          });
          return;
        }
      }
      try {
        const csTools = getCornerstoneTools();
        const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
        for (const ann of allAnns) {
          if (
            ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            ann.data?.segmentation?.segmentIndex === localIndex &&
            isLoadedFromMask(ann)
          ) {
            setLoadedFromMask(ann, false);
            ann.isLocked = false;
            if (ann.annotationUID)
              csTools.annotation.visibility?.setAnnotationVisibility(ann.annotationUID, true);
          }
        }
      } catch (err) {
        logger.debug("[handleEdit] unlock loaded-mask contours failed:", err);
      }

      // Enter edit mode with pre-filled form values
      editingAnnotationIdRef.current = annotation.annotation_id;
      activateSegment(localIndex);
      setSelectedAnnotationId(null);
      setIsAddingNew(true);
      // Session entry point: mint a fresh undo scope for this edit session.
      beginUndoSession(studyInstanceUid);
      setSelectedCategory(annotation.category);
      const safeAttrs: Record<string, AnnotationValue> = {};
      for (const [k, v] of Object.entries(annotation.attributes ?? {})) {
        if (
          typeof v === "string" ||
          typeof v === "boolean" ||
          typeof v === "number" ||
          Array.isArray(v)
        ) {
          safeAttrs[k] = v;
        }
      }
      setFormValues(safeAttrs);
      setAnnotationStartTs(new Date().toISOString());
      setIsSegmentationActive(true);
      setIsDrawing(true);
      setHasFirstStroke(true);

      // Lock to the annotation's series
      setLockedSeriesId(annotation.series_instance_uid);

      // Activate default drawing tool
      queueMicrotask(() => handleSelectCategoryRef.current?.(annotation.category));
      onStartAnnotation?.("edit");
      return true;
    },
    [
      isDrawing,
      caseLocked,
      activateSegment,
      setSelectedAnnotationId,
      setIsSegmentationActive,
      setIsDrawing,
      setHasFirstStroke,
      setLockedSeriesId,
      setAnnotationStartTs,
      studyInstanceUid,
      onCandidateEditStart,
      resolveAnnotationLocalIndex,
      onStartAnnotation,
      savedEditRequest,
      segmentationVolumeId,
    ],
  );

  const handledSavedEdit = useRef<typeof savedEditRequest>(null);
  useEffect(() => {
    if (!savedEditRequest || handledSavedEdit.current === savedEditRequest || isDrawing) return;
    const annotation = savedAnnotations.find(
      (item) => item.annotation_id === savedEditRequest.annotationId,
    );
    if (!annotation) return;
    const contours = getCornerstoneTools().annotation.state.getAllAnnotations() ?? [];
    if (
      !contours.some(
        (item: any) =>
          item.data?.savedAnnotationId === annotation.annotation_id &&
          item.data?.contour?.polyline?.length >= 3,
      )
    )
      return;
    if (handleEdit(annotation)) handledSavedEdit.current = savedEditRequest;
  }, [savedEditRequest, savedAnnotations, contourVersion, isDrawing, handleEdit]);

  // Prediction cards are not backend AnnotationRecords yet, so they cannot
  // call handleEdit above. Enter the exact same edit state from the selected
  // prediction segment instead. The former CaseViewer path dispatched the
  // generic "start annotation" command, which claimed max(segment)+1; Bump
  // then looked for that new segment and reported "No contour" while the
  // selected AI contour remained on its original segment.
  const handledPredictionEditRef = useRef<number | null>(null);
  useEffect(() => {
    if (predictionEditSegmentIndex == null) {
      handledPredictionEditRef.current = null;
      return;
    }
    if (
      handledPredictionEditRef.current === predictionEditSegmentIndex ||
      !predictionEditCategory ||
      !segmentationVolumeId ||
      isDrawing ||
      caseLocked
    ) {
      return;
    }

    const savedAnnotationId = loadSavedMasksByAnnotationId ? predictionEditAnnotationId : null;
    let matchingContours: any[] = [];
    try {
      const annotationState = getCornerstoneTools().annotation.state;
      matchingContours = (annotationState.getAllAnnotations?.() ?? []).filter(
        (ann: any) =>
          ann?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          (savedAnnotationId
            ? ann?.data?.savedAnnotationId === savedAnnotationId
            : ann?.data?.segmentation?.segmentIndex === predictionEditSegmentIndex) &&
          (ann?.data?.contour?.polyline?.length ?? 0) >= 3,
      );
    } catch (err) {
      logger.debug("[prediction edit] contour lookup deferred:", err);
      return;
    }

    // The prediction overlay is populated asynchronously. Do not consume the
    // request until its contours exist; contourVersion changes when they land
    // and retries this effect.
    if (matchingContours.length === 0) return;

    const editSegmentIndex = savedAnnotationId
      ? matchingContours[0].data?.segmentation?.segmentIndex
      : predictionEditSegmentIndex;
    if (
      !Number.isSafeInteger(editSegmentIndex) ||
      editSegmentIndex <= 0 ||
      matchingContours.some((ann) => ann.data?.segmentation?.segmentIndex !== editSegmentIndex)
    )
      return;

    // Unlock only after the checkpoint and ownership handoff succeed.
    if (!getPredictionEditCompletion) return;
    const checkpointReady = savedAnnotationId
      ? onBeforePredictionEdit?.(predictionEditSegmentIndex, {
          annotationId: savedAnnotationId,
          segmentIndex: editSegmentIndex,
        })
      : onBeforePredictionEdit?.(predictionEditSegmentIndex);
    if (!checkpointReady) return;
    const completion = getPredictionEditCompletion(predictionEditSegmentIndex);
    if (!completion) return;
    predictionEditCompletionRef.current = completion;

    for (const ann of matchingContours) {
      setLoadedFromMask(ann, false);
      ann.isLocked = false;
    }

    handledPredictionEditRef.current = predictionEditSegmentIndex;
    // Preserve the annotation identity when editing an accepted prediction.
    editingAnnotationIdRef.current = predictionEditAnnotationId;
    activateSegment(editSegmentIndex);
    setSelectedAnnotationId(null);
    setIsAddingNew(true);
    beginUndoSession(studyInstanceUid);
    setSelectedCategory(predictionEditCategory);
    setFormValues(
      predictionEditReaderConfidence != null
        ? { [READER_CONFIDENCE_KEY]: predictionEditReaderConfidence }
        : {},
    );
    setAnnotationStartTs(new Date().toISOString());
    setIsSegmentationActive(true);
    setIsDrawing(true);
    setHasFirstStroke(true);
    setLockedSeriesId(predictionEditSeriesInstanceUid ?? seriesInstanceUid ?? null);
    onStartAnnotation?.("edit");

    // Arm Lasso first while preserving the selected prediction segment.
    // Other supported tools remain available during the edit session.
    queueMicrotask(() => handleSelectCategoryRef.current?.(predictionEditCategory, "lasso"));
  }, [
    predictionEditSegmentIndex,
    predictionEditAnnotationId,
    predictionEditReaderConfidence,
    loadSavedMasksByAnnotationId,
    predictionEditSeriesInstanceUid,
    predictionEditCategory,
    onBeforePredictionEdit,
    getPredictionEditCompletion,
    segmentationVolumeId,
    contourVersion,
    isDrawing,
    caseLocked,
    seriesInstanceUid,
    studyInstanceUid,
    activateSegment,
    setSelectedAnnotationId,
    setAnnotationStartTs,
    setIsSegmentationActive,
    setIsDrawing,
    setHasFirstStroke,
    setLockedSeriesId,
    onStartAnnotation,
  ]);

  const handleSelect = useCallback(
    (annotation: AnnotationRecord) => {
      // Don't switch annotations while drawing — prevents orphan segments (#156)
      if (isDrawing) return;
      const localIndex = resolveAnnotationLocalIndex(annotation);
      if (localIndex == null) return;

      // The jump below lands instantly, but a saved mask's contours are
      // rehydrated asynchronously (loadMaskAsContours on reload), so the mask
      // can lag a beat behind the slice change. If the contours for this
      // segment aren't on the canvas yet, let the user know they're coming.
      try {
        const contoursReady = (
          (getCornerstoneTools().annotation.state.getAllAnnotations() as any[]) ?? []
        ).some(
          (a: any) =>
            a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
            a?.data?.segmentation?.segmentIndex === localIndex &&
            (a?.data?.contour?.polyline?.length ?? 0) >= 3,
        );
        if (!contoursReady) {
          emit("toast", { message: "Please allow a moment for the mask to load…" });
        }
      } catch (err) {
        logger.warn("handleSelect mask_load_toast_check_failed", err);
      }

      setSelectedAnnotationId(annotation.annotation_id);
      setActiveSegmentIndex(localIndex);
      setIsAddingNew(false);

      // Lock viewport to the annotation's series so the slice nav request is
      // honored by ProtocolViewport (which gates on isLockedViewport || isPriority).
      if (annotation.series_instance_uid) {
        setLockedSeriesId(annotation.series_instance_uid);
      }

      // Defer navigation so React can flush the lockedSeriesId update first —
      // ProtocolViewport gates slice nav on isLockedViewport which needs the
      // new lockedSeriesId to be committed before the nav request arrives.
      const segIdx = localIndex;
      const bbox = annotation.bounding_box;
      setTimeout(() => {
        try {
          const cornerstone = getCornerstone();
          const cornerstoneTools = getCornerstoneTools();

          // Renderer-agnostic single-viewport world jump. VolumeViewports expose
          // jumpToWorld; StackViewports (weak-GPU / non-reconstructable fallback)
          // do NOT — calling vp.jumpToWorld there silently no-ops, so the cross-
          // plane companions never move on the stack path. For stack viewports we
          // mirror Cpu2dStrategy.showWorld: project the world point to the nearest
          // stack index, then setImageIdIndex with a zoom/pan pin (setImageIdIndex's
          // internal resetCamera otherwise drifts toward fit-to-window — the
          // "scroll resets zoom" annotator bug). Returns whether it navigated.
          const jumpViewportToWorld = (vp: any, world: [number, number, number]): boolean => {
            if (typeof vp?.jumpToWorld === "function") {
              try {
                vp.jumpToWorld(world);
                return true;
              } catch (err) {
                logger.warn("[handleSelect] jumpToWorld failed:", err);
                return false;
              }
            }
            if (typeof vp?.setImageIdIndex === "function") {
              try {
                const index = cornerstone.utilities.getClosestStackImageIndexForPoint(world, vp);
                if (index == null || !Number.isFinite(index)) return false;
                const total = vp.getImageIds?.()?.length ?? 0;
                const clamped = total > 0 ? Math.max(0, Math.min(total - 1, index)) : index;
                const pres = vp.getViewPresentation?.();
                vp.setImageIdIndex(clamped);
                if (pres) {
                  vp.setViewPresentation?.(pres);
                  vp.render?.();
                }
                return true;
              } catch (err) {
                logger.warn("[handleSelect] stack world-jump failed:", err);
                return false;
              }
            }
            return false;
          };

          const allAnns = (cornerstoneTools.annotation.state.getAllAnnotations() as any[]) ?? [];
          const matchedAnns = allAnns.filter(
            (a: any) =>
              a?.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
              a?.data?.segmentation?.segmentIndex === segIdx &&
              a?.data?.contour?.polyline?.length >= 3,
          );

          if (matchedAnns.length > 0) {
            const engines = cornerstone.getRenderingEngines?.() ?? [];

            // Compute the annotation's world-space centroid once. Cross-plane
            // viewports (an axial drawing's coronal/sagittal companions) don't
            // share imageIds with the drawn plane, so we can't navigate them
            // via slice-index lookup — fall back to jumpToWorld(centroid),
            // which volume viewports resolve to the matching slice in their
            // own orientation.
            let cx = 0,
              cy = 0,
              cz = 0,
              ptCount = 0;
            for (const ann of matchedAnns) {
              const polyline: number[][] = ann?.data?.contour?.polyline ?? [];
              for (const p of polyline) {
                cx += p[0];
                cy += p[1];
                cz += p[2];
                ptCount++;
              }
            }
            const centroid: [number, number, number] | null =
              ptCount > 0 ? [cx / ptCount, cy / ptCount, cz / ptCount] : null;

            let navigatedAny = false;
            for (const engine of engines) {
              for (const vp of engine.getViewports?.() ?? []) {
                const vpIds: string[] = (vp as any).getImageIds?.() ?? [];
                if (vpIds.length === 0) continue;

                // Prefer the matching-orientation annotation: if any of the
                // saved annotations has a viewPlaneNormal parallel to this
                // viewport's normal, jumpToWorld at that annotation's first
                // polyline point. That lands the viewport exactly on the
                // slice where the contour was saved. Fall back to the global
                // centroid only when no orientation-match exists.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const cam = (vp as any).getCamera?.();
                const vpn = cam?.viewPlaneNormal;
                let target: [number, number, number] | null = null;
                if (vpn && Array.isArray(vpn)) {
                  for (const ann of matchedAnns) {
                    const annNormal = ann?.metadata?.viewPlaneNormal;
                    if (!Array.isArray(annNormal) || annNormal.length !== 3) continue;
                    const dot = Math.abs(
                      vpn[0] * annNormal[0] + vpn[1] * annNormal[1] + vpn[2] * annNormal[2],
                    );
                    if (dot < 0.9) continue;
                    const firstPt = ann?.data?.contour?.polyline?.[0];
                    if (Array.isArray(firstPt) && firstPt.length === 3) {
                      target = firstPt as [number, number, number];
                      break;
                    }
                  }
                }
                if (!target && centroid) target = centroid;

                // World-coordinate jump for every viewport (including the locked
                // one). navigateToSlice goes through getCurrentImageIdIndex which
                // on VolumeViewport is reversed vs vp.getImageIds() position —
                // slice index 31 lands at the opposite end of the volume. World
                // coords are orientation-stable. jumpViewportToWorld routes
                // volume→jumpToWorld and stack→getClosestStackImageIndexForPoint,
                // so the companion panes navigate on BOTH render paths.
                if (target && jumpViewportToWorld(vp, target)) {
                  navigatedAny = true;
                }
              }
            }
            if (navigatedAny) return;
          }

          // Fallback: saved-only annotation with no cornerstone contours in
          // state yet (just-loaded from DB). Jump via world coordinates —
          // navigateToSlice(midSlice) was removed because midSlice is a
          // labelmap IJK index and the bus → setSliceIndex path treats it
          // as a viewport step-index, which is reversed on VolumeViewports
          // whose canonicalized HTJ2K slice order runs opposite the step
          // axis. Result was: view jumped to (N-1-midSlice), then
          // jumpToWorld below corrected it — the user-visible "jumps to
          // wrong slice then to the right one".
          if (bbox) {
            const midSlice = Math.round((bbox.point1_k + bbox.point2_k) / 2);
            const p1i = bbox.point1_i;
            const p2i = bbox.point2_i;
            const p1j = bbox.point1_j;
            const p2j = bbox.point2_j;
            const haveIJ =
              typeof p1i === "number" &&
              typeof p2i === "number" &&
              typeof p1j === "number" &&
              typeof p2j === "number";
            try {
              // Prefer the segmentation volume's imageData when registered, but
              // fall back to any loaded viewport's imageData so a saved-row
              // click that races the segmentation registration (cold reload)
              // still navigates. Previously this branch was fully gated on
              // segmentationVolumeId, so the race silently no-op'd.
              let imageData: any = null;
              if (segmentationVolumeId) {
                const vol = cornerstone.cache.getVolume(segmentationVolumeId) as any;
                if (vol?.imageData?.indexToWorld) imageData = vol.imageData;
              }
              if (!imageData?.indexToWorld) {
                const engines = cornerstone.getRenderingEngines?.() ?? [];
                outer: for (const engine of engines) {
                  for (const vp of engine.getViewports?.() ?? []) {
                    const candidate = (vp as any).getImageData?.()?.imageData;
                    if (candidate?.indexToWorld && candidate?.getDimensions) {
                      imageData = candidate;
                      break outer;
                    }
                  }
                }
              }
              if (imageData?.indexToWorld) {
                // When the bbox lacks i/j, use the volume's *midpoint* (not its
                // [0,0] corner) so companion sagittal/coronal viewports land
                // near the patient centre instead of the edge of the field of
                // view. The slice along the view-plane normal is the same
                // either way (jumpToWorld uses Z-projection); i/j only shifts
                // the in-slice centring on the orthogonal viewports.
                const dims = imageData.getDimensions?.() as [number, number, number] | undefined;
                const fallbackI = dims ? dims[0] / 2 : 0;
                const fallbackJ = dims ? dims[1] / 2 : 0;
                const ijkCentroid: [number, number, number] = haveIJ
                  ? [(p1i + p2i) / 2, (p1j + p2j) / 2, midSlice]
                  : [fallbackI, fallbackJ, midSlice];
                const worldCentroid = imageData.indexToWorld(ijkCentroid);
                const engines = cornerstone.getRenderingEngines?.() ?? [];
                for (const engine of engines) {
                  for (const vp of engine.getViewports?.() ?? []) {
                    jumpViewportToWorld(vp, [worldCentroid[0], worldCentroid[1], worldCentroid[2]]);
                  }
                }
              }
            } catch (err) {
              logger.warn("[handleSelect] bbox->world centroid failed:", err);
            }
          }
        } catch (err) {
          console.warn("[handleSelect] Error:", err);
        }
      }, 50);
    },
    [
      setSelectedAnnotationId,
      setActiveSegmentIndex,
      setLockedSeriesId,
      setCurrentSlice,
      segmentationVolumeId,
      navigateToSlice,
      isDrawing,
      resolveAnnotationLocalIndex,
    ],
  );

  // NOTE: handleInterpolate removed — interpolation is now automatic.
  // The viewer auto-accepts interpolated contours after each drawing via
  // ANNOTATION_COMPLETED event listener.

  // Count distinct slices annotated for the active segment.
  let annotatedSliceCount = 0;
  if (isDrawing) {
    try {
      const csTools = getCornerstoneTools();
      const allAnns: any[] = csTools.annotation.state.getAllAnnotations() ?? [];
      const imageIds = new Set<string>();
      for (const ann of allAnns) {
        if (
          ann.metadata?.toolName === "PlanarFreehandContourSegmentationTool" &&
          ann.data?.segmentation?.segmentIndex === activeSegmentIndex &&
          ann.data?.contour?.polyline?.length >= 3
        ) {
          imageIds.add(ann.metadata?.referencedImageId ?? "");
        }
      }
      annotatedSliceCount = imageIds.size;
    } catch (e) {
      logger.debug("[SegmentationAnnotationPanel] slice count skipped (cornerstone not ready):", e);
    }
  }

  // Compact segmentation studies preserve their established single-slice
  // workflow. Other projects remain driven by segmentation-assist config.
  const minSlicesToSave = compactStudyMode
    ? 1
    : (segAssistConfig?.workflow?.min_slices_to_save ?? 2);
  const canSave =
    isAddingNew &&
    schema &&
    isDrawing &&
    annotationStartTs &&
    (compactStudyMode || isFormComplete(schema, formValues, lightMode)) &&
    annotatedSliceCount >= minSlicesToSave;
  const readerConfidenceMissing = requireReaderConfidence && readerConfidence == null;
  // Editor opened from a card, but entry has not succeeded yet (ENG-186 step 3).
  // The card rail is already locked, so the reader needs Cancel here.
  const predictionEditPending = predictionEditSegmentIndex != null && !isAddingNew;

  return (
    <div
      className={`sidebar-premium sidebar-premium-border-l ${compactStudyMode ? "!h-auto min-h-0" : ""}`}
      data-testid="right-sidebar"
    >
      {/* Single-hue ambient wash */}
      <div className="absolute inset-0 bg-signal-ambient pointer-events-none" />

      {/* Header — navigation */}
      <div className={compactStudyMode ? "hidden" : "relative px-3 py-2 shrink-0 space-y-1.5"}>
        <div className={compactStudyMode ? "hidden" : "flex items-center"}>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrevious}
              disabled={!hasPrevious}
              className="sidebar-btn w-7 h-7"
              title="Previous (←)"
            >
              <FaChevronLeft className="w-3 h-3 text-fg-muted" />
            </button>

            <div className="text-center">
              <span className="text-lg font-bold tracking-tight text-white">
                {currentIndex + 1}
                <span className="text-fg-muted font-light text-sm">/{totalCases}</span>
              </span>
              {modelInitialized && (
                <span className="ml-2 rounded-full bg-signal-tint px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-signal-text-tint">
                  Model initialized
                </span>
              )}
              <span className="ml-2 text-[10px] text-fg-muted" data-testid="saved-annotation-count">
                {savedAnnotations.length} seg{savedAnnotations.length !== 1 ? "s" : ""}
              </span>
            </div>

            {!hideLightMode && (
              <button
                onClick={onNext}
                disabled={!hasNext}
                className="sidebar-btn w-7 h-7"
                title="Next (→)"
              >
                <FaChevronRight className="w-3 h-3 text-fg-muted" />
              </button>
            )}
          </div>

          <div className="flex flex-1 items-center justify-evenly">
            {/* Light / Full attribute view toggle — sits where the Reference
                Lines crosshair used to be (that toggle now lives only in the
                left ViewerToolbar (X)). Light shows each category's quick-capture
                fields; Full reveals the complete set. */}
            <button
              onClick={() => setAttributeViewMode(lightMode ? "full" : "light")}
              className={`sidebar-btn h-7 px-2 flex items-center justify-center text-[10px] font-medium ${
                lightMode ? "text-fg-muted" : "text-signal-text-tint bg-signal-tint"
              }`}
              title={
                lightMode
                  ? "Light view — showing quick-capture fields. Click for full view."
                  : "Full view — showing all fields. Click for light view."
              }
            >
              <FaLayerGroup className="w-3 h-3" />
            </button>
            {/* Quick-reference hover button — shortcuts + contour legend */}
            <div className={compactStudyMode ? "hidden" : "relative group/hint"}>
              <div className="sidebar-btn h-7 px-2 flex items-center justify-center cursor-default text-fg-muted text-[11px] font-medium hover:text-white">
                ?
              </div>
              <div className="absolute right-0 top-8 z-30 hidden group-hover/hint:block text-[9px] text-fg-muted bg-bg-2 border border-white/10 rounded-lg px-3 py-2.5 shadow-xl whitespace-nowrap space-y-2">
                <div className="text-[8px] font-medium text-fg-muted uppercase tracking-wider">
                  Segmentation tools
                </div>
                <div className="space-y-0.5">
                  <div>
                    <kbd className="text-fg-muted">Shift+G</kbd> Region Grow (click stone)
                  </div>
                  <div>
                    <kbd className="text-fg-muted">Shift+E</kbd> Bump / Refine
                  </div>
                  <div>
                    <kbd className="text-fg-muted">Shift+S</kbd> Lasso / Draw
                  </div>
                </div>
                <div className="border-t border-white/10 pt-1.5 text-[8px] font-medium text-fg-muted uppercase tracking-wider">
                  Refinement
                </div>
                <div className="space-y-0.5">
                  <div>
                    <kbd className="text-fg-muted">Shift+Click</kbd> add point{" "}
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 align-middle" />{" "}
                    include
                  </div>
                  <div>
                    <kbd className="text-fg-muted">Ctrl+Click</kbd> add point{" "}
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 align-middle" />{" "}
                    exclude
                  </div>
                </div>
                <div className="border-t border-white/10 pt-1.5 text-[8px] font-medium text-fg-muted uppercase tracking-wider">
                  Navigation
                </div>
                <div className="space-y-0.5">
                  <div>
                    <kbd className="text-fg-muted">Z</kbd> zoom{" "}
                    <kbd className="text-fg-muted">P</kbd> pan
                  </div>
                  <div>
                    <kbd className="text-fg-muted">W</kbd> window/level
                  </div>
                  <div>
                    <kbd className="text-fg-muted">[ / ]</kbd> bump size
                  </div>
                </div>
                <div className="border-t border-white/10 pt-1.5 text-[8px] font-medium text-fg-muted uppercase tracking-wider">
                  Actions
                </div>
                <div className="space-y-0.5">
                  <div>
                    <kbd className="text-fg-muted">Del</kbd> clear slice
                  </div>
                  <div>
                    <kbd className="text-fg-muted">&#8984;Z</kbd> undo (active view)
                  </div>
                  <div>
                    <kbd className="text-fg-muted">Esc</kbd> reject all
                  </div>
                </div>
              </div>
            </div>
            {onWorkList && <WorkListButton onClick={onWorkList} />}
            {!compactStudyMode && onNextUnlabeled && (
              <button
                onClick={onNextUnlabeled}
                disabled={!hasNextUnlabeled}
                className="sidebar-btn-primary h-7 px-3 flex items-center justify-center gap-1 text-[10px]"
                title="Next unlabeled (N)"
              >
                <span>Skip</span>
                <FaChevronRight className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Segmentation tool buttons + Instructions */}
      <div className="relative px-3 pb-1 shrink-0 flex gap-1.5">
        <div className="flex gap-1 flex-1 min-w-0">
          <button
            title="Lasso draw (Shift+S)"
            disabled={
              studyToolPolicy ? !studyToolActions.canSelect : isSaving || !isSegmentationActive
            }
            // Segmentation tools are explicit modes, not toggles. Re-clicking
            // Lasso after a completed stroke must leave it armed for the next
            // stroke instead of silently switching primary mouse back to W/L.
            onClick={() => {
              if (studyToolPolicy) return studyToolActions.selectTool("lasso");
              setIsSegmentationActive(true);
              setActiveMeasurementTool("lasso");
            }}
            className={`flex-1 sidebar-btn flex items-center justify-center h-7 rounded transition-colors ${
              (studyToolPolicy ? !studyToolActions.canSelect : !isSegmentationActive)
                ? "text-fg-muted cursor-not-allowed"
                : activeMeasurementTool === "lasso"
                  ? "bg-signal-tint text-signal-text-tint"
                  : "text-fg-muted hover:text-white"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M2,12 C4,6 7,2 10,4 C13,6 8,10 11,12 C14,14 13,8 14,6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <button
            title={
              (studyToolPolicy ? !studyToolActions.canSelect : !isSegmentationActive)
                ? "Bump/edit contour"
                : "Bump/edit contour (Shift+E) — works on whichever plane has a contour under the cursor"
            }
            disabled={
              studyToolPolicy ? !studyToolActions.canSelect : isSaving || !isSegmentationActive
            }
            onClick={() => {
              if (studyToolPolicy) return studyToolActions.selectTool("bump");
              setIsSegmentationActive(true);
              setActiveMeasurementTool("bump");
            }}
            className={`flex-1 sidebar-btn flex items-center justify-center h-7 rounded transition-colors ${
              !isSegmentationActive
                ? "text-fg-muted cursor-not-allowed"
                : activeMeasurementTool === "bump"
                  ? "bg-signal-tint text-signal-text-tint"
                  : "text-fg-muted hover:text-white"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
            </svg>
          </button>
          <button
            title="Region grow (Shift+G)"
            disabled={
              studyToolPolicy
                ? !studyToolActions.canGrow
                : isSaving ||
                  !isSegmentationActive ||
                  !isV2ToolEnabled(segAssistConfig, "region_grow")
            }
            onClick={() =>
              studyToolPolicy
                ? studyToolActions.toggleRegionGrow()
                : setActiveMeasurementTool(
                    activeMeasurementTool === "regionGrow" ? "none" : "regionGrow",
                  )
            }
            className={`flex-1 sidebar-btn flex items-center justify-center h-7 rounded transition-colors ${
              (
                studyToolPolicy
                  ? !studyToolActions.canGrow
                  : !isSegmentationActive || !isV2ToolEnabled(segAssistConfig, "region_grow")
              )
                ? "text-fg-muted cursor-not-allowed"
                : activeMeasurementTool === "regionGrow"
                  ? "bg-signal-tint text-signal-text-tint"
                  : "text-fg-muted hover:text-white"
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1" opacity="0.7" />
              <circle cx="8" cy="8" r="4" stroke="currentColor" strokeWidth="1" opacity="0.85" />
              <circle cx="8" cy="8" r="1.5" fill="currentColor" opacity="0.9" />
            </svg>
          </button>
          {(!compactStudyMode || allowAiSegmentation) && (
            <button
              title={`Segmentation engine: ${segEngine === "nnInteractive" ? "AI Segmentation" : "Threshold (local)"} — Shift+I to toggle`}
              disabled={studyToolPolicy && !studyToolActions.canSwitchEngine}
              onClick={() => {
                if (studyToolPolicy) return studyToolActions.toggleEngine();
                const next = segEngine === "nnInteractive" ? "threshold" : "nnInteractive";
                setSegEngine(next);
                showNnToast(
                  next === "nnInteractive"
                    ? "Segmentation: AI Segmentation"
                    : "Segmentation: Threshold",
                );
              }}
              className={`flex-1 sidebar-btn flex items-center justify-center h-7 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                segEngine === "nnInteractive"
                  ? "bg-signal-tint text-signal-text-tint"
                  : "text-fg-muted hover:text-white"
              }`}
            >
              {segEngine === "nnInteractive" ? (
                // sparkle / AI
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 1.5 L9.2 6.2 L13.5 8 L9.2 9.8 L8 14.5 L6.8 9.8 L2.5 8 L6.8 6.2 Z"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                // threshold / diamond
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="4"
                    y="4"
                    width="8"
                    height="8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    transform="rotate(45 8 8)"
                  />
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Instructions button — right half */}
        <button
          onClick={() => setShowInstructions(true)}
          className="flex-1 shrink-0 sidebar-btn flex items-center justify-center gap-1.5 h-7 text-[10px] text-fg-muted hover:text-white"
        >
          <FaFileAlt className="w-3 h-3" />
          <span>Instructions</span>
        </button>
        {compactStudyMode && (
          <button
            type="button"
            className="sidebar-btn h-7 w-7 text-[11px] text-fg-muted hover:text-white"
            title="Segmentation help"
            onClick={() => setShowInstructions(true)}
          >
            ?
          </button>
        )}
      </div>

      {/* Mask opacity slider — personalizes contour fill transparency, persisted
          to localStorage via maskFillAlphaAtom. Labeling viewer only. */}
      <div className="px-3 pb-1 shrink-0 flex items-center gap-2">
        <span className="text-[10px] text-fg-muted whitespace-nowrap">Mask opacity</span>
        <input
          type="range"
          min={0}
          max={0.6}
          step={0.05}
          value={maskFillAlpha}
          onChange={(e) => setMaskFillAlpha(Number(e.target.value))}
          aria-label="Mask fill opacity"
          title={`Mask fill opacity: ${Math.round(maskFillAlpha * 100)}%`}
          className="flex-1 h-1 accent-signal-text-tint cursor-pointer"
        />
        <span className="text-[10px] text-fg-muted tabular-nums w-7 text-right">
          {Math.round(maskFillAlpha * 100)}%
        </span>
      </div>

      {compactStudyMode && (
        <div className="relative px-3 pt-1 pb-2 shrink-0">
          {isAddingNew || predictionEditPending ? (
            <>
              {requireReaderConfidence && isAddingNew && (
                <div className="mb-1.5 flex items-start justify-between gap-2">
                  <ReaderConfidenceInput
                    value={String(formValues[READER_CONFIDENCE_KEY] ?? "")}
                    disabled={isSaving}
                    onChange={(value) =>
                      setFormValues((prev) => {
                        const next = { ...prev };
                        if (value === "") delete next[READER_CONFIDENCE_KEY];
                        else next[READER_CONFIDENCE_KEY] = value;
                        return next;
                      })
                    }
                    onSubmit={() => {
                      if (canSave && !isSaving && !unchangedSaving) void handleEditorSave();
                    }}
                  />
                  {readerConfidenceMissing && (
                    <span className="font-mono text-[10px] text-warn">Required to save</span>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  disabled={isSaving || unchangedSaving}
                  className="flex-1 sidebar-btn h-8 text-[11px]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => void handleEditorSave()}
                  disabled={!canSave || isSaving || unchangedSaving || readerConfidenceMissing}
                  className="flex-1 sidebar-btn-primary h-8 flex items-center justify-center gap-1 text-[11px] disabled:opacity-30 disabled:cursor-not-allowed"
                  title={
                    predictionEditPending
                      ? "Preparing editor…"
                      : !canSave
                        ? annotatedSliceCount < minSlicesToSave
                          ? `Draw on at least ${minSlicesToSave} slice${minSlicesToSave === 1 ? "" : "s"} (${annotatedSliceCount} so far)`
                          : "Save annotation"
                        : readerConfidenceMissing
                          ? READER_CONFIDENCE_HINT
                          : "Save annotation"
                  }
                >
                  <FaSave className="w-3 h-3" />
                  Save
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="w-full sidebar-btn-primary h-8 flex items-center justify-center gap-1.5 text-[11px] disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={handleAddNew}
              disabled={caseLocked || !segmentationVolumeId || isDrawing}
              title={
                caseLocked
                  ? lockReason === "complete"
                    ? "Case is marked complete — unmark it to edit"
                    : "Case is marked unassessable — clear your flag to edit"
                  : !segmentationVolumeId
                    ? "Viewport still loading — please wait"
                    : "Start a new annotation"
              }
            >
              <FaPlus className="w-3 h-3" />
              Add Annotation
            </button>
          )}
          {predictionEditPending && (
            <div role="status" className="pt-1.5 text-center text-[10px] text-fg-muted">
              Preparing editor…
            </div>
          )}
        </div>
      )}

      {/* Phase tab bar — phase_dependent projects only (e.g., hl3d-*).
          Sits below Instructions, full-width, matching panel button style. */}
      {gatingMode === GATING_MODE.PHASE_DEPENDENT && (
        <div className="shrink-0">
          <PhaseTabBar />
        </div>
      )}

      {/* Saving indicator */}
      {isSaving && (
        <div className="px-5 py-2.5 text-xs text-signal-text-tint text-center animate-pulse bg-primary/5 border-y border-primary/10">
          Saving...
        </div>
      )}

      {/* Segmentation unavailable warning */}
      {!segmentationVolumeId && !isSaving && (
        <div className="px-3 py-3 text-xs text-fg-muted bg-surface-2 border-y border-border-strong flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-fg-muted animate-pulse shrink-0" />
          <span>
            Loading viewport — segmentation tools will be available once the case finishes loading.
          </span>
        </div>
      )}

      {/* GT Hints info box — pinned above content area so it persists during annotation */}
      {gtHints && gtHints.finding_count > 0 && (
        <div className="shrink-0 px-3 py-2">
          <div className="rounded-md border border-signal-stroke-soft bg-signal-ambient px-3 py-2">
            <div className="text-[10px] font-semibold text-signal-text-tint uppercase tracking-wide mb-1">
              Report Extractions ({gtHints.finding_count} entr
              {gtHints.finding_count !== 1 ? "ies" : "y"})
            </div>
            <div className="text-[11px] text-fg-muted space-y-0.5">
              {gtHints.findings.map((f, i) => {
                const parts: string[] = [f.finding_type.replace(/_/g, " ")];
                if (f.laterality) parts.push(f.laterality);
                // Location slot: renal/urinary (stone) or nodal station (lymph node).
                if (f.nodal_station) parts.push(f.nodal_station.replace(/_/g, " "));
                if (f.renal_location) parts.push(f.renal_location.replace(/_/g, " "));
                if (f.urinary_tract_location)
                  parts.push(f.urinary_tract_location.replace(/_/g, " "));
                if (f.size) parts.push(f.size);
                if (f.lesion_multiplicity) parts.push(f.lesion_multiplicity);
                if (f.malignancy_suspicion) parts.push(f.malignancy_suspicion.replace(/_/g, " "));
                return (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="text-signal-text-tint/60 mt-px">&#8226;</span>
                    <span>{parts.join(" \u00b7 ")}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div
        className={
          compactStudyMode ? "hidden" : "relative flex-1 overflow-y-auto px-3 py-2 min-h-0"
        }
      >
        {/* Saved annotations list — hidden when form is active to save space */}
        {savedAnnotations.length > 0 && !(isAddingNew && selectedCategory) && (
          <div className="space-y-1 mb-3" data-testid="saved-annotations-list">
            <div className="text-[10px] font-medium text-fg-muted uppercase tracking-wider mb-1">
              Annotations ({savedAnnotations.length})
            </div>
            {savedAnnotations.map((annotation) => (
              <AnnotationCard
                key={annotation.annotation_id}
                annotation={annotation}
                conditionLabel={conditionLabels?.[annotation.category]}
                isSelected={selectedAnnotationId === annotation.annotation_id}
                sequenceLabel={annotationSequenceLabels[annotation.annotation_id] ?? ""}
                onSelect={() => handleSelect(annotation)}
                onDelete={() => handleDelete(annotation.annotation_id)}
                onEdit={() => handleEdit(annotation)}
              />
            ))}
          </div>
        )}

        {/* Add new annotation section */}
        {isAddingNew ? (
          <div className="space-y-3">
            {/* Step 1: Category selection (if not yet chosen) */}
            {!selectedCategory ? (
              <div className="space-y-2">
                <div className="text-[10px] font-medium text-fg-muted uppercase tracking-wider">
                  Select annotation type
                </div>
                <div className="space-y-1">
                  {Object.entries(schemas)
                    .filter(([, s]) => !s.hidden)
                    .filter(([key]) => key !== "organ")
                    .map(([key, s]) => (
                      <button
                        key={key}
                        onClick={() => handleSelectCategory(key)}
                        className="w-full sidebar-btn flex items-center gap-2 h-8 px-3 text-[11px] hover:border-primary/30 hover:text-signal-text-tint"
                      >
                        <span className="flex-1 text-left">
                          {conditionLabels
                            ? formatSegmentationCondition(conditionLabels[key] ?? key)
                            : s.display_name}
                        </span>
                        {savedAnnotations.filter((a) => a.category === key).length > 0 && (
                          <span className="text-[9px] text-fg-muted tabular-nums">
                            {savedAnnotations.filter((a) => a.category === key).length} existing
                          </span>
                        )}
                      </button>
                    ))}
                </div>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="w-full sidebar-btn h-7 text-[10px] text-fg-muted"
                >
                  Cancel
                </button>
              </div>
            ) : !schema ? (
              /* Selected category has no matching schema (e.g. annotation saved
                 under a category not in this project's labels_to_assign). Show
                 a recoverable message instead of crashing on schema!.display_name. */
              <div className="space-y-2">
                <div className="text-[11px] text-fg-muted">
                  Category &ldquo;{selectedCategory}&rdquo; is not available in this project&apos;s
                  schema.
                </div>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="w-full sidebar-btn h-7 text-[10px] text-fg-muted"
                >
                  Cancel
                </button>
              </div>
            ) : (
              /* Step 2: Drawing + attribute form (category selected) */
              <div className="space-y-3" data-testid="annotation-form">
                <div className={compactStudyMode ? "hidden" : "flex items-center gap-2 mb-2"}>
                  <div
                    className="w-3 h-3 rounded-xs shrink-0"
                    style={{ backgroundColor: getCategoryColor(category ?? "") }}
                  />
                  <span className="text-[11px] font-medium text-white/90">
                    {conditionLabels
                      ? formatSegmentationCondition(
                          conditionLabels[category ?? ""] ?? category ?? "",
                        )
                      : schema.display_name}{" "}
                    #{newSequenceNumber}
                  </span>
                  {isDrawing && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-signal-tint text-signal-text-tint">
                      Drawing
                    </span>
                  )}
                </div>

                {/* Init from previous annotation — pre-fill attributes */}
                {(() => {
                  const sameCategoryAnns = savedAnnotations
                    .filter((a) => a.category === selectedCategory)
                    .sort((a, b) => b.annotated_at.localeCompare(a.annotated_at));
                  const seen = new Set<string>();
                  const candidates: AnnotationRecord[] = [];
                  for (const ann of sameCategoryAnns) {
                    const key = JSON.stringify(ann.attributes);
                    if (!seen.has(key)) {
                      seen.add(key);
                      candidates.push(ann);
                      if (candidates.length >= 3) break;
                    }
                  }
                  if (candidates.length === 0 || Object.keys(formValues).length > 0) return null;
                  return (
                    <div className="text-[10px] text-fg-muted bg-white/5 rounded-lg px-3 py-2.5 space-y-1">
                      <div className="font-medium text-fg-muted">Init from previous...</div>
                      {candidates.map((ann, idx) => (
                        <button
                          key={ann.annotation_id}
                          onClick={() => setFormValues({ ...ann.attributes })}
                          className="w-full flex items-center gap-2 h-7 px-2 rounded hover:bg-white/10 transition-colors"
                        >
                          <div
                            className="w-2 h-2 rounded-xs shrink-0"
                            style={{ backgroundColor: getCategoryColor(ann.category) }}
                          />
                          <span className="flex-1 text-left truncate text-fg-muted">
                            {formatAttributeSummary(ann.attributes, schema ?? undefined)}
                          </span>
                          <span className="text-[9px] text-fg-muted shrink-0">#{idx + 1}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}

                {/* Attribute form */}
                {!compactStudyMode && (
                  <SegmentationAnnotationForm
                    schema={schema}
                    values={formValues}
                    onChange={handleFieldChange}
                    measurements={derivedMeasurements}
                    disabled={isSaving}
                    lightMode={lightMode}
                  />
                )}

                {/* Save / Cancel buttons */}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleCancel}
                    disabled={isSaving || unchangedSaving}
                    className="flex-1 sidebar-btn h-8 text-[11px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => void handleEditorSave()}
                    disabled={!canSave || isSaving || unchangedSaving}
                    className="flex-1 sidebar-btn-primary h-8 flex items-center justify-center gap-1 text-[11px] disabled:opacity-30 disabled:cursor-not-allowed"
                    title={
                      !canSave
                        ? annotatedSliceCount < minSlicesToSave
                          ? `Draw on at least ${minSlicesToSave} slice${minSlicesToSave === 1 ? "" : "s"} (${annotatedSliceCount} so far)`
                          : "Fill all required fields"
                        : "Save annotation"
                    }
                  >
                    <FaSave className="w-3 h-3" />
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Add button — disabled while drawing to prevent orphan segments (#156) */}
            <button
              className={
                compactStudyMode
                  ? "hidden"
                  : "w-full sidebar-btn flex items-center justify-center gap-1.5 h-8 text-[11px] hover:border-primary/30 hover:text-signal-text-tint disabled:opacity-30 disabled:cursor-not-allowed"
              }
              onClick={handleAddNew}
              disabled={!segmentationVolumeId || isDrawing}
              title={
                !segmentationVolumeId
                  ? "Viewport still loading — please wait"
                  : isDrawing
                    ? "Complete or save the current annotation first"
                    : "Start a new annotation"
              }
            >
              <FaPlus className="w-3 h-3" />
              Add Annotation
            </button>
          </>
        )}
      </div>

      {/* A complete study case can't also be flagged: Unmark first. An
          unassessable case keeps the control, which holds "Clear my flag". */}
      {!isAddingNew && !undoVisible && lockReason !== "complete" && (
        <UnassessableCaseControl
          projectId={projectId}
          studyInstanceUid={studyInstanceUid}
          dispositions={currentCaseStatus?.unassessable_dispositions}
          disabled={isDrawing || isSaving || markCompleteMutation.isPending}
        />
      )}

      {/* Segment Organ — available whenever an assigned category's organ_policy
          allows it (C2). Sits directly above Mark Complete. Starts an organ-mask
          annotation with the nnInteractive region-grow tool; the annotator clicks
          the parenchyma. When a required-organ annotation is saved but the organ mask
          isn't, this step replaces Mark Complete and is emphasized (C3). */}
      {!compactStudyMode && !isAddingNew && !undoVisible && !isCaseUnassessable && organEnabled && (
        <div className="shrink-0 px-3 pt-2">
          <button
            onClick={handleSegmentOrgan}
            disabled={!segmentationVolumeId || isDrawing}
            className={
              organGateActive
                ? "w-full flex items-center justify-center gap-1.5 h-8 text-[11px] font-medium rounded-md border transition-all duration-150 bg-signal-ambient border-signal-stroke-soft text-signal-text-tint hover:bg-signal-tint hover:border-signal-stroke disabled:opacity-30 disabled:cursor-not-allowed"
                : "w-full sidebar-btn flex items-center justify-center gap-1.5 h-8 text-[11px] hover:border-primary/30 hover:text-signal-text-tint disabled:opacity-30 disabled:cursor-not-allowed"
            }
            title={
              !segmentationVolumeId
                ? "Viewport still loading — please wait"
                : isDrawing
                  ? "Finish the current annotation first"
                  : organGateActive
                    ? "An organ mask is required before this case can be completed"
                    : "Auto-segment the organ, then review/correct"
            }
          >
            <FaCrosshairs className="w-3 h-3" />
            {organGateActive ? "Segment Organ (required)" : "Segment Organ"}
          </button>
        </div>
      )}

      {/* Footer — Mark Complete / Undo pinned to bottom. Hidden while a required
          organ mask is still missing (C3): the Segment Organ step above stands in
          its place until the organ is saved. */}
      {!compactStudyMode &&
        !isAddingNew &&
        savedAnnotations.length > 0 &&
        !undoVisible &&
        !organGateActive &&
        !isCaseUnassessable &&
        !isCaseComplete && (
          <div className="shrink-0 px-3 py-2 border-t border-white/5">
            <button
              onClick={() => completeCase()}
              disabled={markCompleteMutation.isPending}
              className="w-full h-8 text-[11px] font-medium rounded-md border transition-all duration-150 bg-signal-ambient border-signal-stroke-soft text-signal-text-tint hover:bg-signal-tint hover:border-signal-stroke disabled:opacity-30"
            >
              <FaCheck className="w-3 h-3 inline mr-1.5" />
              {markCompleteMutation.isPending ? "Marking..." : "Mark Complete"}
            </button>
          </div>
        )}
      {/* Escape hatch for cases with no annotations — typically studies with too
          many stones to reasonably segment. Lets the reader complete the case so
          it leaves their worklist (no annotation is saved). Only shown once the
          annotations query has resolved with zero saved annotations, so it never
          flashes while a case that does have saved stones is still loading.
          Sends reason "numerous_stones" to mark-complete so the backend can tell
          this skip apart from a genuine no-findings completion (the case is still
          marked complete either way). */}
      {!compactStudyMode &&
        !isAddingNew &&
        annotationsQuery.isSuccess &&
        savedAnnotations.length === 0 &&
        (annotationsQuery.data?.annotations?.length ?? 0) === 0 &&
        !undoVisible &&
        !organGateActive &&
        !isCaseUnassessable &&
        !isCaseComplete && (
          <div className="shrink-0 px-3 py-2 border-t border-white/5">
            <button
              onClick={() => completeCase("numerous_stones")}
              disabled={markCompleteMutation.isPending}
              className="w-full h-8 text-[11px] font-medium rounded-md border border-border-strong bg-surface-2 text-fg-muted transition-all duration-150 hover:bg-white/10 hover:text-fg disabled:opacity-30"
              title="No stones segmented — skip this case (e.g. too many stones to annotate). It will be marked complete and leave your worklist; no annotation is saved."
            >
              <FaStepForward className="w-3 h-3 inline mr-1.5" />
              {markCompleteMutation.isPending ? "Skipping..." : "Skip — numerous stones"}
            </button>
          </div>
        )}
      {undoVisible && (
        <div className="shrink-0 px-3 py-2 border-t border-white/5">
          <button
            onClick={() => {
              if (markCompleteTimerRef.current) clearTimeout(markCompleteTimerRef.current);
              setUndoVisible(false);
              unmarkCompleteMutation.mutate();
            }}
            className="w-full h-8 text-[11px] font-medium rounded-md border transition-all duration-150 bg-surface-2 border-border-strong text-fg hover:bg-white/10 hover:border-border-strong"
          >
            Marked complete — Undo
          </button>
        </div>
      )}
      {/* #726: revisiting an already-complete case shows a Completed/Unmark
          state instead of "Mark Complete" (which read as still-unlabeled). */}
      {!isAddingNew && !undoVisible && isCaseComplete && (
        <div className="shrink-0 px-3 py-2 border-t border-white/5">
          <button
            onClick={() => unmarkCompleteMutation.mutate()}
            disabled={unmarkCompleteMutation.isPending}
            className="w-full h-8 text-[11px] font-medium rounded-md border transition-all duration-150 bg-surface-2 border-border-strong text-fg hover:bg-white/10 hover:border-border-strong disabled:opacity-30"
            title="This case is marked complete — click to unmark"
          >
            <FaCheck className="w-3 h-3 inline mr-1.5" />
            {unmarkCompleteMutation.isPending ? "Unmarking..." : "Completed — Unmark"}
          </button>
        </div>
      )}

      <InstructionsDialog
        projectId={projectId}
        instructionsKey={instructionsKey}
        open={showInstructions}
        onClose={() => setShowInstructions(false)}
      />
    </div>
  );
}
